/**
 * Root route table for gozem-admin-web-v2.
 *
 * Shape: one root record for `login`, two for the MFA pending
 * flow (`mfa/challenge`, `mfa/enroll`), then a single authenticated
 * shell (`MainLayout`) whose `children[]` register every migrated
 * v2 page as a lazy `loadComponent`. Anything not yet migrated
 * lives in `main-layout` sidebar as a `disabled: true` placeholder
 * and does NOT appear here (see `layout/main-layout/main-layout.ts`
 * `NavGroup` — the sidebar is the source of truth for "what's
 * un-migrated").
 *
 * Guard stack per child route:
 *   - `authGuard` sits on the shell as `canActivate` + `canActivateChild`,
 *     so it re-checks the session on every child navigation (an access
 *     token that expired while idle goes to /login?next=<clicked page>).
 *   - `permissionGuard("<token>")` gates each child individually. The
 *     token names mirror the RBAC v2 catalog served by
 *     `/rbac/me/permissions`. Any admin without the token is bounced
 *     to `/dashboard`. See `core/guards/permission.guard.ts`.
 *   - `mfaPendingGuard` gates ONLY the two mfa/* records — it accepts
 *     the pending_mfa JWT and rejects fully-authenticated admins.
 *
 * Wildcard `path: "**"` redirects unknown URLs to `""` (which then
 * redirects to `/dashboard` inside the shell) — mirroring legacy
 * `gozem-admin-web` behaviour of never 404-ing for a signed-in admin.
 */
import {Routes} from "@angular/router";
import {authGuard, mfaPendingGuard, permissionGuard} from "./core/guards";

export const routes: Routes = [
    {
        path: "login",
        loadComponent: () => import("./pages/login/login").then((m) => m.LoginPage)
    },
    // MFA post-SSO flow — reached by admin_sso.js returning
    // mfa_stage=challenge/enroll. `mfaPendingGuard` accepts only
    // holders of the pending_mfa JWT; a fully-authenticated admin is
    // bounced to /dashboard, an unauthenticated caller to /login.
    {
        path: "mfa/challenge",
        canActivate: [mfaPendingGuard],
        loadComponent: () => import("./pages/mfa-challenge/mfa-challenge").then((m) => m.MfaChallengePage)
    },
    {
        path: "mfa/enroll",
        canActivate: [mfaPendingGuard],
        loadComponent: () => import("./pages/mfa-enroll/mfa-enroll").then((m) => m.MfaEnrollPage)
    },
    {
        path: "",
        canActivate: [authGuard],
        canActivateChild: [authGuard],
        loadComponent: () => import("./layout/main-layout/main-layout").then((m) => m.MainLayout),
        children: [
            {
                path: "",
                pathMatch: "full",
                redirectTo: "dashboard"
            },
            {
                path: "dashboard",
                loadComponent: () => import("./pages/dashboard/dashboard").then((m) => m.DashboardPage)
            },
            {
                // Create + edit are opened as modals inside the list page
                // (gz-modal-shell), so the dedicated /new and /:id routes are
                // gone. The list page handles every CRUD action.
                path: "corporate-rule-types",
                canActivate: [permissionGuard("corporate_rule_types:list")],
                loadComponent: () =>
                    import("./pages/corporate-rule-types-list/corporate-rule-types-list").then(
                        (m) => m.CorporateRuleTypesListPage
                    )
            },
            {
                path: "audio-templates",
                canActivate: [permissionGuard("audio_templates:list")],
                loadComponent: () =>
                    import("./pages/audio-templates-list/audio-templates-list").then((m) => m.AudioTemplatesListPage)
            },
            {
                path: "reason-tags",
                canActivate: [permissionGuard("reason_tags:list")],
                loadComponent: () =>
                    import("./pages/reason-tags-list/reason-tags-list").then((m) => m.ReasonTagsListPage)
            },
            {
                path: "landmarks",
                canActivate: [permissionGuard("landmarks:list")],
                loadComponent: () => import("./pages/landmarks-list/landmarks-list").then((m) => m.LandmarksListPage)
            },
            {
                path: "dispatchers",
                canActivate: [permissionGuard("dispatchers:list")],
                loadComponent: () =>
                    import("./pages/dispatchers-list/dispatchers-list").then((m) => m.DispatchersListPage)
            },
            {
                path: "dispatchers/new",
                canActivate: [permissionGuard("dispatchers:create")],
                loadComponent: () =>
                    import("./pages/dispatchers-form/dispatchers-form").then((m) => m.DispatchersFormPage)
            },
            {
                path: "dispatchers/:id",
                canActivate: [permissionGuard("dispatchers:update")],
                loadComponent: () =>
                    import("./pages/dispatchers-form/dispatchers-form").then((m) => m.DispatchersFormPage)
            },
            {
                path: "cups",
                canActivate: [permissionGuard("cups:list")],
                loadComponent: () => import("./pages/cups-list/cups-list").then((m) => m.CupsListPage)
            },
            {
                path: "languages",
                canActivate: [permissionGuard("languages:list")],
                loadComponent: () => import("./pages/languages-list/languages-list").then((m) => m.LanguagesListPage)
            },
            // ── Reference-data quick wins (May 2026 batch) ──
            // 8 list pages flipped on once the @gozem-api/* backends were
            // verified. Each opens its own modal-based CRUD; no dedicated
            // create/edit routes for now (same pattern as corporate-rule-types).
            {
                path: "cities",
                canActivate: [permissionGuard("city:list")],
                loadComponent: () => import("./pages/cities-list/cities-list").then((m) => m.CitiesListPage)
            },
            {
                path: "countries",
                canActivate: [permissionGuard("country:list")],
                loadComponent: () => import("./pages/countries-list/countries-list").then((m) => m.CountriesListPage)
            },
            {
                path: "currencies",
                canActivate: [permissionGuard("currencies:list")],
                loadComponent: () => import("./pages/currencies-list/currencies-list").then((m) => m.CurrenciesListPage)
            },
            {
                path: "tags",
                canActivate: [permissionGuard("tags:list")],
                loadComponent: () => import("./pages/tags-list/tags-list").then((m) => m.TagsListPage)
            },
            {
                path: "departments",
                canActivate: [permissionGuard("departments:list")],
                loadComponent: () =>
                    import("./pages/departments-list/departments-list").then((m) => m.DepartmentsListPage)
            },
            {
                path: "rating-tags",
                canActivate: [permissionGuard("rating_tag:list")],
                loadComponent: () =>
                    import("./pages/rating-tags-list/rating-tags-list").then((m) => m.RatingTagsListPage)
            },
            {
                path: "rating-reasons",
                canActivate: [permissionGuard("rating_reasons:list")],
                loadComponent: () =>
                    import("./pages/rating-reasons-list/rating-reasons-list").then((m) => m.RatingReasonsListPage)
            },
            {
                path: "rating-categories",
                canActivate: [permissionGuard("rating_categories:list")],
                loadComponent: () =>
                    import("./pages/rating-categories-list/rating-categories-list").then(
                        (m) => m.RatingCategoriesListPage
                    )
            },
            // ── Wave 1 batch A + B + C (May 2026) ──
            // Templates (email + training), vehicle catalog (makes / models /
            // types), onboarding (steps + flows). All extend the modal-based
            // CRUD pattern; no dedicated create/edit routes.
            {
                path: "email-templates",
                canActivate: [permissionGuard("email:list")],
                loadComponent: () =>
                    import("./pages/email-templates-list/email-templates-list").then((m) => m.EmailTemplatesListPage)
            },
            {
                path: "training-templates",
                canActivate: [permissionGuard("training_template:list")],
                loadComponent: () =>
                    import("./pages/training-templates-list/training-templates-list").then(
                        (m) => m.TrainingTemplatesListPage
                    )
            },
            {
                path: "vehicle-makes",
                canActivate: [permissionGuard("vehicle_make_model:list")],
                loadComponent: () =>
                    import("./pages/vehicle-makes-list/vehicle-makes-list").then((m) => m.VehicleMakesListPage)
            },
            {
                path: "vehicle-models",
                canActivate: [permissionGuard("vehicle_make_model:list")],
                loadComponent: () =>
                    import("./pages/vehicle-models-list/vehicle-models-list").then((m) => m.VehicleModelsListPage)
            },
            {
                path: "vehicle-types",
                canActivate: [permissionGuard("service_types:list")],
                loadComponent: () =>
                    import("./pages/vehicle-types-list/vehicle-types-list").then((m) => m.VehicleTypesListPage)
            },
            {
                path: "onboarding-steps",
                canActivate: [permissionGuard("onboarding_steps:list")],
                loadComponent: () =>
                    import("./pages/onboarding-steps-list/onboarding-steps-list").then((m) => m.OnboardingStepsListPage)
            },
            {
                path: "onboarding-flows",
                canActivate: [permissionGuard("onboarding_flows:list")],
                loadComponent: () =>
                    import("./pages/onboarding-flows-list/onboarding-flows-list").then((m) => m.OnboardingFlowsListPage)
            },
            // ── Wave 1 batch D · merchant catalog (May 2026) ──
            // 7 pages. Two are read-only (activities + to-rewards) — those
            // pages omit the create button and row actions.
            {
                path: "merchant-categories",
                canActivate: [permissionGuard("merchant_categories:list")],
                loadComponent: () =>
                    import("./pages/merchant-categories-list/merchant-categories-list").then(
                        (m) => m.MerchantCategoriesListPage
                    )
            },
            {
                path: "merchant-groups",
                canActivate: [permissionGuard("merchant_groups:list")],
                loadComponent: () =>
                    import("./pages/merchant-groups-list/merchant-groups-list").then((m) => m.MerchantGroupsListPage)
            },
            {
                path: "merchant-services",
                canActivate: [permissionGuard("merchant_service:list")],
                loadComponent: () =>
                    import("./pages/merchant-services-list/merchant-services-list").then(
                        (m) => m.MerchantServicesListPage
                    )
            },
            {
                path: "merchant-activities",
                canActivate: [permissionGuard("merchant_activities:list")],
                loadComponent: () =>
                    import("./pages/merchant-activities-list/merchant-activities-list").then(
                        (m) => m.MerchantActivitiesListPage
                    )
            },
            {
                path: "merchant-actions",
                canActivate: [permissionGuard("merchant_actions:list")],
                loadComponent: () =>
                    import("./pages/merchant-actions-list/merchant-actions-list").then((m) => m.MerchantActionsListPage)
            },
            {
                path: "merchant-action-types",
                canActivate: [permissionGuard("merchant_action_types:list")],
                loadComponent: () =>
                    import("./pages/merchant-action-types-list/merchant-action-types-list").then(
                        (m) => m.MerchantActionTypesListPage
                    )
            },
            {
                path: "merchant-to-rewards",
                canActivate: [permissionGuard("merchant_to_rewards:list")],
                loadComponent: () =>
                    import("./pages/merchant-to-rewards-list/merchant-to-rewards-list").then(
                        (m) => m.MerchantToRewardsListPage
                    )
            },
            // ── Dedicated edit/new routes (replacing thin modals) ──
            // The 5 pages below all have a legacy admin edit flow that
            // navigates to a separate full-page form (anywhere from 389 to
            // 3,124 LOC). v2 mirrors that with dedicated routes so the
            // editor has room for the real field set + sectioned cards +
            // tabs. The /…/new path creates; the /…/:id path edits.
            //
            // List pages remain at the unsuffixed path (cities, countries,
            // merchant-services, training-templates, vehicle-types).
            {
                path: "cities/new",
                canActivate: [permissionGuard("city:create")],
                loadComponent: () => import("./pages/cities-form/cities-form").then((m) => m.CitiesFormPage)
            },
            {
                path: "cities/:id",
                canActivate: [permissionGuard("city:update")],
                loadComponent: () => import("./pages/cities-form/cities-form").then((m) => m.CitiesFormPage)
            },
            {
                path: "countries/new",
                canActivate: [permissionGuard("country:create")],
                loadComponent: () => import("./pages/countries-form/countries-form").then((m) => m.CountriesFormPage)
            },
            {
                path: "countries/:id",
                // Every guard must pass: edit needs :update, and the form loads GET /countries/:id (country:view).
                canActivate: [permissionGuard("country:update"), permissionGuard("country:view")],
                loadComponent: () => import("./pages/countries-form/countries-form").then((m) => m.CountriesFormPage)
            },
            {
                path: "merchant-services/new",
                canActivate: [permissionGuard("merchant_service:create")],
                loadComponent: () =>
                    import("./pages/merchant-services-form/merchant-services-form").then(
                        (m) => m.MerchantServicesFormPage
                    )
            },
            {
                path: "merchant-services/:id",
                canActivate: [permissionGuard("merchant_service:update")],
                loadComponent: () =>
                    import("./pages/merchant-services-form/merchant-services-form").then(
                        (m) => m.MerchantServicesFormPage
                    )
            },
            {
                path: "training-templates/new",
                canActivate: [permissionGuard("training_template:create")],
                loadComponent: () =>
                    import("./pages/training-templates-form/training-templates-form").then(
                        (m) => m.TrainingTemplatesFormPage
                    )
            },
            {
                path: "training-templates/:id",
                canActivate: [permissionGuard("training_template:update")],
                loadComponent: () =>
                    import("./pages/training-templates-form/training-templates-form").then(
                        (m) => m.TrainingTemplatesFormPage
                    )
            },
            {
                path: "vehicle-types/new",
                canActivate: [permissionGuard("service_types:create")],
                loadComponent: () =>
                    import("./pages/vehicle-types-form/vehicle-types-form").then((m) => m.VehicleTypesFormPage)
            },
            {
                path: "vehicle-types/:id",
                canActivate: [permissionGuard("service_types:update")],
                loadComponent: () =>
                    import("./pages/vehicle-types-form/vehicle-types-form").then((m) => m.VehicleTypesFormPage)
            },
            // ── Trip reference batch (June 2026) ──
            // custom_fees + points_of_interest + routes (intercity). PoIs
            // stays modal CRUD (legacy modal-in-place); custom-fees and
            // routes use dedicated form routes since the legacy ships
            // separate add_*_form / edit_*_form files. The 4 remaining
            // trip-reference candidates (cancelation_reasons, trip_intentions,
            // schedules, vehicle_service_groups) wait on their @gozem-api/*
            // modules — see migration plan for the deferred list.
            {
                path: "custom-fees",
                canActivate: [permissionGuard("custom_fees:list")],
                loadComponent: () =>
                    import("./pages/custom-fees-list/custom-fees-list").then((m) => m.CustomFeesListPage)
            },
            {
                path: "custom-fees/new",
                canActivate: [permissionGuard("custom_fees:create")],
                loadComponent: () =>
                    import("./pages/custom-fees-form/custom-fees-form").then((m) => m.CustomFeesFormPage)
            },
            {
                // Lows-links — legacy custom_fees_list.html:387-391 « Voir »: the form read-only.
                path: "custom-fees/:id/view",
                canActivate: [permissionGuard("custom_fees:view")],
                data: {readOnly: true},
                loadComponent: () =>
                    import("./pages/custom-fees-form/custom-fees-form").then((m) => m.CustomFeesFormPage)
            },
            {
                path: "custom-fees/:id",
                canActivate: [permissionGuard("custom_fees:update")],
                loadComponent: () =>
                    import("./pages/custom-fees-form/custom-fees-form").then((m) => m.CustomFeesFormPage)
            },
            {
                path: "points-of-interest",
                canActivate: [permissionGuard("points_of_interest:list")],
                loadComponent: () =>
                    import("./pages/points-of-interest-list/points-of-interest-list").then(
                        (m) => m.PointsOfInterestListPage
                    )
            },
            {
                path: "routes",
                canActivate: [permissionGuard("routes:list")],
                loadComponent: () => import("./pages/routes-list/routes-list").then((m) => m.RoutesListPage)
            },
            {
                path: "routes/new",
                canActivate: [permissionGuard("routes:create")],
                loadComponent: () => import("./pages/routes-form/routes-form").then((m) => m.RoutesFormPage)
            },
            {
                // Lows-links — legacy route_list.html:401-407 « Voir »: the route form read-only.
                path: "routes/:id/view",
                canActivate: [permissionGuard("routes:view")],
                data: {readOnly: true},
                loadComponent: () => import("./pages/routes-form/routes-form").then((m) => m.RoutesFormPage)
            },
            {
                path: "routes/:id",
                canActivate: [permissionGuard("routes:update")],
                loadComponent: () => import("./pages/routes-form/routes-form").then((m) => m.RoutesFormPage)
            },
            // ── Reviews + market intelligence batch (June 2026) ──
            // 2 modal-CRUD pages live; reviews itself is backend-pending
            // (no @gozem-api/review module — legacy reads Reviews directly
            // from Mongo). See SKILL.gozem-admin-web-migrator for the
            // backend-pending list.
            {
                path: "market-competitors",
                canActivate: [permissionGuard("market_competitors:list")],
                loadComponent: () =>
                    import("./pages/market-competitors-list/market-competitors-list").then(
                        (m) => m.MarketCompetitorsListPage
                    )
            },
            {
                path: "market-competitors/new",
                canActivate: [permissionGuard("market_competitors:create")],
                loadComponent: () =>
                    import("./pages/market-competitors-form/market-competitors-form").then(
                        (m) => m.MarketCompetitorsFormPage
                    )
            },
            {
                path: "market-competitors/:id",
                canActivate: [permissionGuard("market_competitors:update")],
                loadComponent: () =>
                    import("./pages/market-competitors-form/market-competitors-form").then(
                        (m) => m.MarketCompetitorsFormPage
                    )
            },
            {
                path: "driver-identifications",
                canActivate: [permissionGuard("driver_identifications:list")],
                loadComponent: () =>
                    import("./pages/driver-identifications-list/driver-identifications-list").then(
                        (m) => m.DriverIdentificationsListPage
                    )
            },
            {
                path: "driver-identifications/new",
                canActivate: [permissionGuard("driver_identifications:create")],
                loadComponent: () =>
                    import("./pages/driver-identifications-form/driver-identifications-form").then(
                        (m) => m.DriverIdentificationsFormPage
                    )
            },
            {
                path: "driver-identifications/:id",
                canActivate: [permissionGuard("driver_identifications:update")],
                loadComponent: () =>
                    import("./pages/driver-identifications-form/driver-identifications-form").then(
                        (m) => m.DriverIdentificationsFormPage
                    )
            },
            // ── Driver lifecycle config batch (June 2026) ──
            // 2 pages live: driver-availabilities (read-only supply
            // analytics) and provider-pauses (catalog of pause types,
            // modal CRUD). minimum-activity was scoped out — it's a
            // composite multi-endpoint page (6 sub-routes) and needs
            // its own dedicated batch.
            {
                path: "driver-availabilities",
                canActivate: [permissionGuard("driver_availabilities:list")],
                loadComponent: () =>
                    import("./pages/driver-availabilities-list/driver-availabilities-list").then(
                        (m) => m.DriverAvailabilitiesListPage
                    )
            },
            {
                path: "provider-pauses",
                canActivate: [permissionGuard("provider_pauses:list")],
                loadComponent: () =>
                    import("./pages/provider-pauses-list/provider-pauses-list").then((m) => m.ProviderPausesListPage)
            },
            // Provider pause queue + history — read-only views over the
            // legacy `provider_pause_request` and `provider_pause_history`
            // collections. v2 ships them as inspection pages; the
            // validation workflow (approve / reject / extend / end /
            // stop) stays in the legacy admin until the full pipeline
            // ports across (pause balance + change logs + email template).
            {
                path: "provider-pause-requests",
                canActivate: [permissionGuard("provider_pause_requests:list")],
                loadComponent: () =>
                    import("./pages/provider-pause-requests-list/provider-pause-requests-list").then(
                        (m) => m.ProviderPauseRequestsListPage
                    )
            },
            {
                path: "provider-pause-histories",
                canActivate: [permissionGuard("provider_pause_histories:list")],
                loadComponent: () =>
                    import("./pages/provider-pause-histories-list/provider-pause-histories-list").then(
                        (m) => m.ProviderPauseHistoriesListPage
                    )
            },
            // Asset tracking audit log — every device interaction (block,
            // unblock, track, alert). Read-only; block/unblock actions
            // remain in the legacy admin because they push to the
            // trasolux / device-of-things API which isn't wired to v2.
            {
                path: "asset-tracking-logs",
                canActivate: [permissionGuard("asset_tracking_logs:list")],
                loadComponent: () =>
                    import("./pages/asset-tracking-logs-list/asset-tracking-logs-list").then(
                        (m) => m.AssetTrackingLogsListPage
                    )
            },
            // ── Driver / session / block / wallet-sign log batch ──
            // 4 high-volume log views — full legacy column set, search
            // selector, date filters, multi-selects (online-log: cities +
            // vehicle types), row actions (drivers-log: per-action menu,
            // temp-block-log: unblock).
            {
                path: "drivers-logs",
                canActivate: [permissionGuard("drivers_logs:list")],
                loadComponent: () =>
                    import("./pages/drivers-logs-list/drivers-logs-list").then((m) => m.DriversLogsListPage)
            },
            {
                path: "change-logs",
                canActivate: [permissionGuard("change_logs:list")],
                loadComponent: () =>
                    import("./pages/change-logs-list/change-logs-list").then((m) => m.ChangeLogsListPage)
            },
            {
                path: "admin-logs",
                canActivate: [permissionGuard("admin_logs:list")],
                loadComponent: () => import("./pages/admin-logs-list/admin-logs-list").then((m) => m.AdminLogsListPage)
            },
            {
                path: "city-type-logs",
                canActivate: [permissionGuard("city_type_logs:list")],
                loadComponent: () =>
                    import("./pages/city-type-logs-list/city-type-logs-list").then((m) => m.CityTypeLogsListPage)
            },
            {
                path: "order-correction-logs",
                canActivate: [permissionGuard("order_correction_logs:list")],
                loadComponent: () =>
                    import("./pages/order-correction-logs-list/order-correction-logs-list").then(
                        (m) => m.OrderCorrectionLogsListPage
                    )
            },
            {
                path: "rider-logs",
                canActivate: [permissionGuard("rider_logs:list")],
                loadComponent: () => import("./pages/rider-logs-list/rider-logs-list").then((m) => m.RiderLogsListPage)
            },
            {
                path: "notification-logs",
                canActivate: [permissionGuard("notification_logs:list")],
                loadComponent: () =>
                    import("./pages/notification-logs-list/notification-logs-list").then(
                        (m) => m.NotificationLogsListPage
                    )
            },
            {
                path: "install-source-logs",
                canActivate: [permissionGuard("install_source_logs:list")],
                loadComponent: () =>
                    import("./pages/install-source-logs-list/install-source-logs-list").then(
                        (m) => m.InstallSourceLogsListPage
                    )
            },
            {
                path: "promo-code-logs",
                canActivate: [permissionGuard("promo_code_logs:list")],
                loadComponent: () =>
                    import("./pages/promo-code-logs-list/promo-code-logs-list").then((m) => m.PromoCodeLogsListPage)
            },
            {
                path: "promo-usage-attempts-logs",
                canActivate: [permissionGuard("promo_usage_attempts_logs:list")],
                loadComponent: () =>
                    import("./pages/promo-usage-attempts-logs-list/promo-usage-attempts-logs-list").then(
                        (m) => m.PromoUsageAttemptsLogsListPage
                    )
            },
            {
                path: "financing-contract-signature-logs",
                canActivate: [permissionGuard("financing_contract_signature_logs:list")],
                loadComponent: () =>
                    import(
                        "./pages/financing-contract-signature-logs-list/financing-contract-signature-logs-list"
                    ).then((m) => m.FinancingContractSignatureLogsListPage)
            },
            {
                path: "crm-campaigns",
                canActivate: [permissionGuard("crm_campaigns:list")],
                loadComponent: () =>
                    import("./pages/crm-campaigns-list/crm-campaigns-list").then((m) => m.CrmCampaignsListPage)
            },
            {
                path: "crm-campaigns/new",
                canActivate: [permissionGuard("crm_campaigns:create")],
                loadComponent: () =>
                    import("./pages/crm-campaigns-form/crm-campaigns-form").then((m) => m.CrmCampaignsFormPage)
            },
            {
                // Lows-links — legacy crm_campaigns.html:710 « Voir le détail » (view_campaign): the form read-only.
                path: "crm-campaigns/:id/view",
                canActivate: [permissionGuard("crm_campaigns:view")],
                data: {readOnly: true},
                loadComponent: () =>
                    import("./pages/crm-campaigns-form/crm-campaigns-form").then((m) => m.CrmCampaignsFormPage)
            },
            {
                path: "crm-campaigns/:id",
                canActivate: [permissionGuard("crm_campaigns:update")],
                loadComponent: () =>
                    import("./pages/crm-campaigns-form/crm-campaigns-form").then((m) => m.CrmCampaignsFormPage)
            },
            {
                path: "crm-campaign-logs",
                canActivate: [permissionGuard("crm_campaign_logs:list")],
                loadComponent: () =>
                    import("./pages/crm-campaign-logs-list/crm-campaign-logs-list").then(
                        (m) => m.CrmCampaignLogsListPage
                    )
            },
            {
                path: "crm-campaign-change-logs",
                canActivate: [permissionGuard("crm_campaign_change_logs:list")],
                loadComponent: () =>
                    import("./pages/crm-campaign-change-logs-list/crm-campaign-change-logs-list").then(
                        (m) => m.CrmCampaignChangeLogsListPage
                    )
            },
            {
                path: "merchant-preparation-time-logs",
                canActivate: [permissionGuard("merchant_preparation_time_logs:list")],
                loadComponent: () =>
                    import("./pages/merchant-preparation-time-logs-list/merchant-preparation-time-logs-list").then(
                        (m) => m.MerchantPreparationTimeLogsListPage
                    )
            },
            {
                path: "twilio-call-logs",
                canActivate: [permissionGuard("twilio_call_logs:list")],
                loadComponent: () =>
                    import("./pages/twilio-call-logs-list/twilio-call-logs-list").then((m) => m.TwilioCallLogsListPage)
            },
            {
                path: "twilio-chats",
                canActivate: [permissionGuard("twilio_chats:list")],
                loadComponent: () =>
                    import("./pages/twilio-chats-list/twilio-chats-list").then((m) => m.TwilioChatsListPage)
            },
            {
                path: "admin-users-logs",
                canActivate: [permissionGuard("admin_users_logs:list")],
                loadComponent: () =>
                    import("./pages/admin-users-logs-list/admin-users-logs-list").then((m) => m.AdminUsersLogsListPage)
            },
            {
                // RBAC access-log viewer. Read-only. Gated by
                // `admin_access_log:list` — sysadmins only. Non-super
                // admins should NOT see the sidebar entry either
                // (main-layout's fail-closed nav filter handles it).
                path: "admin-access-logs",
                canActivate: [permissionGuard("admin_access_log:list")],
                loadComponent: () =>
                    import("./pages/admin-access-logs-list/admin-access-logs-list").then(
                        (m) => m.AdminAccessLogsListPage
                    )
            },
            {
                path: "admin-group-logs",
                canActivate: [permissionGuard("admin_group_logs:list")],
                loadComponent: () =>
                    import("./pages/admin-group-logs-list/admin-group-logs-list").then((m) => m.AdminGroupLogsListPage)
            },
            {
                path: "department-logs",
                canActivate: [permissionGuard("department_logs:list")],
                loadComponent: () =>
                    import("./pages/department-logs-list/department-logs-list").then((m) => m.DepartmentLogsListPage)
            },
            {
                path: "merchant-group-logs",
                canActivate: [permissionGuard("merchant_group_logs:list")],
                loadComponent: () =>
                    import("./pages/merchant-group-logs-list/merchant-group-logs-list").then(
                        (m) => m.MerchantGroupLogsListPage
                    )
            },
            {
                path: "login-logs",
                canActivate: [permissionGuard("login_logs:list")],
                loadComponent: () => import("./pages/login-logs-list/login-logs-list").then((m) => m.LoginLogsListPage)
            },
            {
                path: "user-leads",
                canActivate: [permissionGuard("user_leads:list")],
                loadComponent: () => import("./pages/user-leads-list/user-leads-list").then((m) => m.UserLeadsListPage)
            },
            {
                path: "connected-users",
                canActivate: [permissionGuard("connected_users:list")],
                loadComponent: () =>
                    import("./pages/connected-users-list/connected-users-list").then((m) => m.ConnectedUsersListPage)
            },
            {
                path: "favorite-blacklist-champions",
                canActivate: [permissionGuard("favorite_blacklist_champions:list")],
                loadComponent: () =>
                    import("./pages/favorite-blacklist-champions-list/favorite-blacklist-champions-list").then(
                        (m) => m.FavoriteBlacklistChampionsListPage
                    )
            },
            {
                path: "online-logs",
                canActivate: [permissionGuard("online_logs:list")],
                loadComponent: () =>
                    import("./pages/online-logs-list/online-logs-list").then((m) => m.OnlineLogsListPage)
            },
            {
                path: "temporary-block-logs",
                canActivate: [permissionGuard("temporary_block_logs:list")],
                loadComponent: () =>
                    import("./pages/temporary-block-logs-list/temporary-block-logs-list").then(
                        (m) => m.TemporaryBlockLogsListPage
                    )
            },
            {
                path: "wallet-sign-update-logs",
                canActivate: [permissionGuard("wallet_sign_update_logs:list")],
                loadComponent: () =>
                    import("./pages/wallet-sign-update-logs-list/wallet-sign-update-logs-list").then(
                        (m) => m.WalletSignUpdateLogsListPage
                    )
            },
            // ── Driver lifecycle batch (June 2026) ──
            // 4 champion-domain list pages: certificate grants, document
            // uploads, vehicle inventory, provider-service catalog. All four
            // are read-only listings with multi-select filters and row
            // actions (edit / approve / toggle / view-documents cross-link)
            // gated to TODOs until v2 backend exposes the underlying
            // action endpoints (legacy uses POST form actions).
            {
                path: "driver-certificates",
                canActivate: [permissionGuard("driver_certificates:list")],
                loadComponent: () =>
                    import("./pages/driver-certificates-list/driver-certificates-list").then(
                        (m) => m.DriverCertificatesListPage
                    )
            },
            {
                path: "driver-documents",
                canActivate: [permissionGuard("driver_documents:list")],
                loadComponent: () =>
                    import("./pages/driver-documents-list/driver-documents-list").then((m) => m.DriverDocumentsListPage)
            },
            {
                path: "driver-vehicles",
                canActivate: [permissionGuard("driver_vehicles:list")],
                loadComponent: () =>
                    import("./pages/driver-vehicles-list/driver-vehicles-list").then((m) => m.DriverVehiclesListPage)
            },
            {
                path: "provider-services",
                canActivate: [permissionGuard("provider_services:list")],
                loadComponent: () =>
                    import("./pages/provider-services-list/provider-services-list").then(
                        (m) => m.ProviderServicesListPage
                    )
            },
            // Phase 1 scaffold for the dedicated provider-services editor
            // (task #533). Mirrors merchant-services-form routing. Backend
            // save/update endpoints are DESIGN DEBT — see the form
            // component docblock. Same component handles both /new (create)
            // and /:id (edit); the paramMap decides.
            {
                path: "provider-services/new",
                canActivate: [permissionGuard("provider_services:create")],
                loadComponent: () =>
                    import("./pages/provider-services-form/provider-services-form").then(
                        (m) => m.ProviderServicesFormPage
                    )
            },
            {
                path: "provider-services/:id",
                canActivate: [permissionGuard("provider_services:update")],
                loadComponent: () =>
                    import("./pages/provider-services-form/provider-services-form").then(
                        (m) => m.ProviderServicesFormPage
                    )
            },
            // ── Champion incentives / certificate templates / leads
            //     / provider-pause validation settings (June 2026) ──
            // Four legacy provider pages: bonus ledger, certificate
            // catalog, lead pipeline, and the singleton pause-validation
            // settings page. Read-only listings; create/edit/validate
            // mutations stay in the legacy admin pending workflow port.
            {
                path: "incentives",
                canActivate: [permissionGuard("incentives:list")],
                loadComponent: () => import("./pages/incentives-list/incentives-list").then((m) => m.IncentivesListPage)
            },
            {
                path: "certificates",
                canActivate: [permissionGuard("certificates:list")],
                loadComponent: () =>
                    import("./pages/certificates-list/certificates-list").then((m) => m.CertificatesListPage)
            },
            {
                path: "certificates/new",
                canActivate: [permissionGuard("certificates:create")],
                loadComponent: () =>
                    import("./pages/certificates-form/certificates-form").then((m) => m.CertificatesFormPage)
            },
            {
                path: "certificates/:id",
                canActivate: [permissionGuard("certificates:update")],
                loadComponent: () =>
                    import("./pages/certificates-form/certificates-form").then((m) => m.CertificatesFormPage)
            },
            {
                path: "provider-leads",
                canActivate: [permissionGuard("provider_leads:list")],
                loadComponent: () =>
                    import("./pages/provider-leads-list/provider-leads-list").then((m) => m.ProviderLeadsListPage)
            },
            {
                path: "cancelation-reasons",
                canActivate: [permissionGuard("cancelation_reasons:list")],
                loadComponent: () =>
                    import("./pages/cancelation-reasons-report/cancelation-reasons-report").then(
                        (m) => m.CancelationReasonsReportPage
                    )
            },
            {
                path: "historical-location",
                canActivate: [permissionGuard("historical_location:list")],
                loadComponent: () =>
                    import("./pages/historical-location-list/historical-location-list").then(
                        (m) => m.HistoricalLocationListPage
                    )
            },
            {
                path: "bank-loans",
                canActivate: [permissionGuard("bank_loans:list")],
                loadComponent: () => import("./pages/bank-loans-list/bank-loans-list").then((m) => m.BankLoansListPage)
            },
            {
                path: "bank-loans/new",
                canActivate: [permissionGuard("bank_loans:create")],
                loadComponent: () => import("./pages/bank-loans-form/bank-loans-form").then((m) => m.BankLoansFormPage)
            },
            {
                path: "bank-loans/:id",
                canActivate: [permissionGuard("bank_loans:update")],
                loadComponent: () => import("./pages/bank-loans-form/bank-loans-form").then((m) => m.BankLoansFormPage)
            },
            {
                path: "gozem-products",
                canActivate: [permissionGuard("products:list")],
                loadComponent: () =>
                    import("./pages/gozem-products-list/gozem-products-list").then((m) => m.GozemProductsListPage)
            },
            {
                path: "incoming-trips",
                canActivate: [permissionGuard("incoming_trips:list")],
                loadComponent: () =>
                    import("./pages/incoming-trips-list/incoming-trips-list").then((m) => m.IncomingTripsListPage)
            },
            {
                path: "user-tracking",
                canActivate: [permissionGuard("user_tracking:list")],
                loadComponent: () =>
                    import("./pages/user-tracking-list/user-tracking-list").then((m) => m.UserTrackingListPage)
            },
            {
                path: "reviews",
                canActivate: [permissionGuard("reviews:list")],
                loadComponent: () => import("./pages/reviews-list/reviews-list").then((m) => m.ReviewsListPage)
            },
            {
                path: "overdrafts",
                canActivate: [permissionGuard("overdrafts:list")],
                loadComponent: () => import("./pages/overdrafts-list/overdrafts-list").then((m) => m.OverdraftsListPage)
            },
            {
                path: "merchants-logs",
                canActivate: [permissionGuard("merchants_logs:list")],
                loadComponent: () =>
                    import("./pages/merchants-logs-list/merchants-logs-list").then((m) => m.MerchantsLogsListPage)
            },
            {
                path: "dynamic-links",
                canActivate: [permissionGuard("dynamic_links:list")],
                loadComponent: () =>
                    import("./pages/dynamic-links-list/dynamic-links-list").then((m) => m.DynamicLinksListPage)
            },
            {
                path: "deeplink",
                canActivate: [permissionGuard("campaigns:list")],
                loadComponent: () => import("./pages/deeplink-list/deeplink-list").then((m) => m.DeeplinkListPage)
            },
            {
                path: "teams",
                canActivate: [permissionGuard("teams:list")],
                loadComponent: () => import("./pages/teams-list/teams-list").then((m) => m.TeamsListPage)
            },
            {
                path: "competition-templates",
                canActivate: [permissionGuard("competition_templates:list")],
                loadComponent: () =>
                    import("./pages/competition-templates-list/competition-templates-list").then(
                        (m) => m.CompetitionTemplatesListPage
                    )
            },
            {
                path: "competitions",
                canActivate: [permissionGuard("competitions:list")],
                loadComponent: () =>
                    import("./pages/competitions-list/competitions-list").then((m) => m.CompetitionsListPage)
            },
            {
                path: "competitions/new",
                canActivate: [permissionGuard("competitions:create")],
                loadComponent: () =>
                    import("./pages/competitions-form/competitions-form").then((m) => m.CompetitionsFormPage)
            },
            {
                path: "competitions/:id",
                canActivate: [permissionGuard("competitions:update")],
                loadComponent: () =>
                    import("./pages/competitions-form/competitions-form").then((m) => m.CompetitionsFormPage)
            },
            {
                path: "segment-logs",
                canActivate: [permissionGuard("segment_logs:list")],
                loadComponent: () =>
                    import("./pages/segment-logs-list/segment-logs-list").then((m) => m.SegmentLogsListPage)
            },
            {
                path: "token-retirement-logs",
                canActivate: [permissionGuard("token_retirement_logs:list")],
                loadComponent: () =>
                    import("./pages/token-retirement-logs-list/token-retirement-logs-list").then(
                        (m) => m.TokenRetirementLogsListPage
                    )
            },
            {
                path: "trip-subscriptions",
                canActivate: [permissionGuard("trip_subscriptions:list")],
                loadComponent: () =>
                    import("./pages/trip-subscriptions-list/trip-subscriptions-list").then(
                        (m) => m.TripSubscriptionsListPage
                    )
            },
            {
                path: "trip-subscriptions/new",
                canActivate: [permissionGuard("trip_subscriptions:create")],
                loadComponent: () =>
                    import("./pages/trip-subscriptions-form/trip-subscriptions-form").then(
                        (m) => m.TripSubscriptionsFormPage
                    )
            },
            {
                path: "trip-subscriptions/:id",
                canActivate: [permissionGuard("trip_subscriptions:update")],
                loadComponent: () =>
                    import("./pages/trip-subscriptions-form/trip-subscriptions-form").then(
                        (m) => m.TripSubscriptionsFormPage
                    )
            },
            {
                path: "gowin-services",
                canActivate: [permissionGuard("gowin_services:list")],
                loadComponent: () =>
                    import("./pages/gowin-services-list/gowin-services-list").then((m) => m.GowinServicesListPage)
            },
            {
                path: "gowin-services/new",
                canActivate: [permissionGuard("gowin_services:create")],
                loadComponent: () =>
                    import("./pages/gowin-services-form/gowin-services-form").then((m) => m.GowinServicesFormPage)
            },
            {
                path: "gowin-services/:id",
                canActivate: [permissionGuard("gowin_services:update")],
                loadComponent: () =>
                    import("./pages/gowin-services-form/gowin-services-form").then((m) => m.GowinServicesFormPage)
            },
            // Wave 10 B19 — legacy /gowin_services/curate?id= (section curation).
            {
                path: "gowin-services/:id/sections",
                canActivate: [permissionGuard("gowin_services:update")],
                loadComponent: () =>
                    import("./pages/type-section-curation/type-section-curation").then(
                        (m) => m.TypeSectionCurationPage
                    ),
                data: {kind: "gowin"}
            },
            {
                path: "expense-payment-general-settings",
                canActivate: [permissionGuard("configuration:list")],
                loadComponent: () =>
                    import("./pages/expense-payment-general-settings/expense-payment-general-settings").then(
                        (m) => m.ExpensePaymentGeneralSettingsPage
                    )
            },
            {
                path: "recovery-control",
                canActivate: [permissionGuard("recovery_control:list")],
                loadComponent: () =>
                    import("./pages/recovery-control-page/recovery-control-page").then((m) => m.RecoveryControlPage)
            },
            {
                path: "trip-intentions",
                canActivate: [permissionGuard("trip_intentions:list")],
                loadComponent: () =>
                    import("./pages/trip-intentions-list/trip-intentions-list").then((m) => m.TripIntentionsListPage)
            },
            {
                path: "product-validation-log",
                canActivate: [permissionGuard("product_validation_log:list")],
                loadComponent: () =>
                    import("./pages/product-validation-log-list/product-validation-log-list").then(
                        (m) => m.ProductValidationLogListPage
                    )
            },
            {
                path: "product-validation",
                canActivate: [permissionGuard("products:list")],
                loadComponent: () =>
                    import("./pages/product-validation-list/product-validation-list").then(
                        (m) => m.ProductValidationListPage
                    )
            },
            {
                path: "dispatch-trips-request",
                canActivate: [permissionGuard("dispatch_trips_request:list")],
                loadComponent: () =>
                    import("./pages/dispatch-trips-request-list/dispatch-trips-request-list").then(
                        (m) => m.DispatchTripsRequestListPage
                    )
            },
            {
                path: "survey-campaigns",
                canActivate: [permissionGuard("survey_campaigns:list")],
                loadComponent: () =>
                    import("./pages/survey-campaigns-list/survey-campaigns-list").then((m) => m.SurveyCampaignsListPage)
            },
            {
                path: "dispatch-create-trips",
                canActivate: [permissionGuard("dispatch_create_trips:list")],
                loadComponent: () =>
                    import("./pages/dispatch-create-trips-page/dispatch-create-trips-page").then(
                        (m) => m.DispatchCreateTripsPage
                    )
            },
            {
                path: "user-mapview",
                canActivate: [permissionGuard("user_mapview:list")],
                loadComponent: () =>
                    import("./pages/user-mapview-page/user-mapview-page").then((m) => m.UserMapviewPage)
            },
            {
                path: "live-map",
                canActivate: [permissionGuard("live_map:list")],
                loadComponent: () => import("./pages/live-map-page/live-map-page").then((m) => m.LiveMapPage)
            },
            {
                // v2 of legacy /mapview ("Live Champion Map").
                path: "champions-mapview",
                canActivate: [permissionGuard("live_map:list")],
                loadComponent: () =>
                    import("./pages/champions-mapview/champions-mapview").then((m) => m.ChampionsMapviewPage)
            },
            {
                // Live monitoring batch — Transport & Ecommerce Activities.
                path: "live-monitoring",
                canActivate: [permissionGuard("live_monitoring:list")],
                loadComponent: () =>
                    import("./pages/live-monitoring-page/live-monitoring-page").then((m) => m.LiveMonitoringPage)
            },
            {
                // Live monitoring batch — Champions Activities.
                path: "champions-activities",
                canActivate: [permissionGuard("champion_activities:list")],
                loadComponent: () =>
                    import("./pages/champions-activities-page/champions-activities-page").then(
                        (m) => m.ChampionsActivitiesPage
                    )
            },
            {
                // Live monitoring batch — Dispatch center live monitoring.
                path: "dispatch-live-monitoring",
                canActivate: [permissionGuard("dispatch_live_monitoring:list")],
                loadComponent: () =>
                    import("./pages/dispatch-live-monitoring-page/dispatch-live-monitoring-page").then(
                        (m) => m.DispatchLiveMonitoringPage
                    )
            },
            {
                // Historical events — per-user master-detail timeline page.
                // Backs @gozem-api/historical-event (GET /historical-events).
                path: "historical-events",
                canActivate: [permissionGuard("historical_events:list")],
                loadComponent: () =>
                    import("./pages/historical-events-page/historical-events-page").then((m) => m.HistoricalEventsPage)
            },
            {
                path: "stock-movements",
                canActivate: [permissionGuard("stock_movements:list")],
                loadComponent: () =>
                    import("./pages/stock-movements-list/stock-movements-list").then((m) => m.StockMovementsListPage)
            },
            {
                path: "gozem-assets",
                canActivate: [permissionGuard("gozem_assets:list")],
                loadComponent: () =>
                    import("./pages/gozem-assets-list/gozem-assets-list").then((m) => m.GozemAssetsListPage)
            },
            // Asset invoices form — dedicated route for the
            // "Ajouter coût additionnel" row action on gozem-assets-list.
            // Mirrors legacy add_asset_invoices.html (365 LOC).
            {
                path: "asset-invoices/:asset_id",
                canActivate: [permissionGuard("gozem_assets:update")],
                loadComponent: () =>
                    import("./pages/asset-invoices-form/asset-invoices-form").then((m) => m.AssetInvoicesFormPage)
            },
            {
                path: "budget-validation-tools",
                canActivate: [permissionGuard("budget_validation_tools:list")],
                loadComponent: () =>
                    import("./pages/budget-validation-tools-list/budget-validation-tools-list").then(
                        (m) => m.BudgetValidationToolsListPage
                    )
            },
            {
                path: "crm-execution",
                canActivate: [permissionGuard("crm_execution:list")],
                loadComponent: () =>
                    import("./pages/crm-execution-list/crm-execution-list").then((m) => m.CrmExecutionListPage)
            },
            {
                path: "notification-center",
                canActivate: [permissionGuard("notification_center:list")],
                loadComponent: () =>
                    import("./pages/notification-center-list/notification-center-list").then(
                        (m) => m.NotificationCenterListPage
                    )
            },
            {
                path: "home-page-promotions",
                canActivate: [permissionGuard("notification_center:list")],
                loadComponent: () =>
                    import("./pages/home-page-promotions-list/home-page-promotions-list").then(
                        (m) => m.HomePagePromotionsListPage
                    )
            },
            {
                // Home-page-promotions form Phase 2 — dedicated create/edit
                // route (the legacy modal carried ~50 fields, well over the
                // per-migrator-skill threshold for lifting into a route).
                path: "home-page-promotions/new",
                canActivate: [permissionGuard("home_page_promotions:create")],
                loadComponent: () =>
                    import("./pages/home-page-promotions-form/home-page-promotions-form").then(
                        (m) => m.HomePagePromotionsFormPage
                    )
            },
            {
                path: "home-page-promotions/:id",
                canActivate: [permissionGuard("home_page_promotions:update")],
                loadComponent: () =>
                    import("./pages/home-page-promotions-form/home-page-promotions-form").then(
                        (m) => m.HomePagePromotionsFormPage
                    )
            },
            {
                // Gowin Home Page Promotions — same @gozem-api/notification-center
                // backend, `context=gowin_homepage_promotions` narrows the
                // gateway_type filter to `gowin_home_page` only (Rule 1I —
                // extension of an existing sibling module).
                path: "gowin-homepage-promotions",
                canActivate: [permissionGuard("notification_center:list")],
                loadComponent: () =>
                    import("./pages/gowin-homepage-promotions-list/gowin-homepage-promotions-list").then(
                        (m) => m.GowinHomepagePromotionsListPage
                    )
            },
            {
                // Gowin Homepage Promotion form — dedicated create route,
                // backs the POST /gowin-homepage-promotions endpoint that
                // pins gateway_type = "gowin_home_page" server-side.
                path: "gowin-homepage-promotions/new",
                canActivate: [permissionGuard("gowin_homepage_promotions:create")],
                loadComponent: () =>
                    import("./pages/gowin-homepage-promotions-form/gowin-homepage-promotions-form").then(
                        (m) => m.GowinHomepagePromotionsFormPage
                    )
            },
            {
                path: "gowin-homepage-promotions/:id",
                canActivate: [permissionGuard("gowin_homepage_promotions:update")],
                loadComponent: () =>
                    import("./pages/gowin-homepage-promotions-form/gowin-homepage-promotions-form").then(
                        (m) => m.GowinHomepagePromotionsFormPage
                    )
            },
            {
                path: "gozem-orders",
                canActivate: [permissionGuard("gozem_orders:list")],
                loadComponent: () =>
                    import("./pages/gozem-orders-list/gozem-orders-list").then((m) => m.GozemOrdersListPage)
            },
            {
                path: "advertising-orders",
                canActivate: [permissionGuard("advertising_orders:list")],
                loadComponent: () =>
                    import("./pages/advertising-orders-list/advertising-orders-list").then(
                        (m) => m.AdvertisingOrdersListPage
                    )
            },
            {
                path: "advertising-products",
                canActivate: [permissionGuard("advertising_products:list")],
                loadComponent: () =>
                    import("./pages/advertising-products-list/advertising-products-list").then(
                        (m) => m.AdvertisingProductsListPage
                    )
            },
            {
                // Advertising product add form — Tier-XL migration,
                // Phase 1 scaffold. Loads advertising-products-form in
                // create mode. Piggybacks on the generic /products
                // backend (product_type=7 branch).
                path: "advertising-products/new",
                canActivate: [permissionGuard("advertising_products:create")],
                loadComponent: () =>
                    import("./pages/advertising-products-form/advertising-products-form").then(
                        (m) => m.AdvertisingProductsFormPage
                    )
            },
            {
                // Advertising product edit form — same component, edit
                // mode detected from paramMap.
                path: "advertising-products/:id",
                canActivate: [permissionGuard("advertising_products:update")],
                loadComponent: () =>
                    import("./pages/advertising-products-form/advertising-products-form").then(
                        (m) => m.AdvertisingProductsFormPage
                    )
            },
            {
                path: "advertising-sold-products",
                canActivate: [permissionGuard("advertising_sold_products:list")],
                loadComponent: () =>
                    import("./pages/advertising-sold-products-list/advertising-sold-products-list").then(
                        (m) => m.AdvertisingSoldProductsListPage
                    )
            },
            {
                // Giant #1 — v2 of legacy /merchants (row actions native).
                path: "merchants",
                canActivate: [permissionGuard("merchants:list")],
                loadComponent: () => import("./pages/merchants-list/merchants-list").then((m) => m.MerchantsListPage)
            },
            {
                // Wave 10 (B6) — "Add Merchant" / "Add Gozem Money agent"
                // (`?agent=1`): legacy merchant.html:98-111 (merchants "c")
                // → add_merchant.html. The edit form in create mode, POST /merchants.
                path: "merchants/new",
                canActivate: [permissionGuard("merchants:create")],
                data: {create: true},
                loadComponent: () => import("./pages/merchants-form/merchants-form").then((m) => m.MerchantsFormPage)
            },
            {
                // Batch 8 (GPGT-39279) — dedicated merchant edit form
                // (`/merchants/:id`). Replaces the legacy 1050-LOC
                // in-place `edit_merchant.html` modal. Creation is
                // `merchants/new` above (Wave 10).
                path: "merchants/:id",
                canActivate: [permissionGuard("merchants:update")],
                loadComponent: () => import("./pages/merchants-form/merchants-form").then((m) => m.MerchantsFormPage)
            },
            {
                // Giant #4 — v2 of legacy /products (row actions native).
                path: "products",
                canActivate: [permissionGuard("products:list")],
                loadComponent: () => import("./pages/products-list/products-list").then((m) => m.ProductsListPage)
            },
            {
                // Product add form — Tier-3 migration, Phase 1 scaffold.
                // Loads products-form in create mode. Backend currently
                // returns 501 Not Implemented for POST /products.
                path: "products/new",
                canActivate: [permissionGuard("products:create")],
                loadComponent: () => import("./pages/products-form/products-form").then((m) => m.ProductsFormPage)
            },
            {
                // Product edit form — same component, edit mode
                // detected from paramMap. Backend currently returns 501
                // Not Implemented for PATCH /products/:id.
                path: "products/:id/edit",
                canActivate: [permissionGuard("products:view")],
                loadComponent: () => import("./pages/products-form/products-form").then((m) => m.ProductsFormPage)
            },
            {
                // Giant #5 — v2 of legacy /sold_products (row actions native).
                path: "sold-products",
                canActivate: [permissionGuard("sold_products:list")],
                loadComponent: () =>
                    import("./pages/sold-products-list/sold-products-list").then((m) => m.SoldProductsListPage)
            },
            {
                // Giant #6 — v2 of legacy /transactions (backed by
                // Wallet_history; row actions native).
                path: "transactions",
                canActivate: [permissionGuard("transactions:list")],
                loadComponent: () =>
                    import("./pages/transactions-list/transactions-list").then((m) => m.TransactionsListPage)
            },
            {
                // Mop-up #11 — list-only v2 of legacy /sb_transactions
                // (backed by sandbox_wallet_history). Row actions stay in
                // legacy.
                path: "sb-transactions",
                canActivate: [permissionGuard("transactions:list")],
                loadComponent: () =>
                    import("./pages/sb-transactions-list/sb-transactions-list").then((m) => m.SbTransactionsListPage)
            },
            {
                // Mop-up #11 — list-only v2 of legacy /driver_earning
                // (backed by provider_daily_analytic). Row actions stay in
                // legacy.
                path: "driver-earning",
                canActivate: [permissionGuard("driver_earning:list")],
                loadComponent: () =>
                    import("./pages/driver-earning-list/driver-earning-list").then((m) => m.DriverEarningListPage)
            },
            {
                // Mop-up — single-driver dashboard (replaces legacy /driver_earning_v2).
                path: "champion-statistics",
                canActivate: [permissionGuard("champion_statistics:list")],
                loadComponent: () =>
                    import("./pages/champion-statistics-page/champion-statistics-page").then(
                        (m) => m.ChampionStatisticsPage
                    )
            },
            {
                // Mop-up — Swagger UI of the orchestrator OpenAPI spec.
                path: "api-docs",
                canActivate: [permissionGuard("api_docs:list")],
                loadComponent: () => import("./pages/api-docs-page/api-docs-page").then((m) => m.ApiDocsPage)
            },
            // 5 API console pages — backed by @gozem-api/api-platform.
            {
                path: "api-services",
                canActivate: [permissionGuard("api_services:list")],
                loadComponent: () =>
                    import("./pages/api-services-list/api-services-list").then((m) => m.ApiServicesListPage)
            },
            {
                path: "api-services/new",
                canActivate: [permissionGuard("api_services:create")],
                loadComponent: () =>
                    import("./pages/api-services-form/api-services-form").then((m) => m.ApiServicesFormPage)
            },
            {
                path: "api-services/:id",
                canActivate: [permissionGuard("api_services:update")],
                loadComponent: () =>
                    import("./pages/api-services-form/api-services-form").then((m) => m.ApiServicesFormPage)
            },
            {
                path: "api-endpoints",
                canActivate: [permissionGuard("api_endpoints:list")],
                loadComponent: () =>
                    import("./pages/api-endpoints-list/api-endpoints-list").then((m) => m.ApiEndpointsListPage)
            },
            {
                path: "api-clients",
                canActivate: [permissionGuard("api_clients:list")],
                loadComponent: () =>
                    import("./pages/api-clients-list/api-clients-list").then((m) => m.ApiClientsListPage)
            },
            {
                path: "api-webhooks",
                canActivate: [permissionGuard("api_webhooks:list")],
                loadComponent: () =>
                    import("./pages/api-webhooks-list/api-webhooks-list").then((m) => m.ApiWebhooksListPage)
            },
            {
                path: "api-webhooks/new",
                canActivate: [permissionGuard("api_webhooks:create")],
                loadComponent: () =>
                    import("./pages/api-webhooks-form/api-webhooks-form").then((m) => m.ApiWebhooksFormPage)
            },
            {
                path: "api-webhooks/:id",
                canActivate: [permissionGuard("api_webhooks:update")],
                loadComponent: () =>
                    import("./pages/api-webhooks-form/api-webhooks-form").then((m) => m.ApiWebhooksFormPage)
            },
            {
                path: "api-request-logs",
                canActivate: [permissionGuard("api_request_logs:list")],
                loadComponent: () =>
                    import("./pages/api-request-logs-list/api-request-logs-list").then((m) => m.ApiRequestLogsListPage)
            },
            {
                // Mop-up #11 — list-only v2 of legacy /gozem_money_user_services.
                path: "gozem-money-user-services",
                canActivate: [permissionGuard("gozem_money_user_services:list")],
                loadComponent: () =>
                    import("./pages/gozem-money-user-services-list/gozem-money-user-services-list").then(
                        (m) => m.GozemMoneyUserServicesListPage
                    )
            },
            {
                // Phase 1 scaffold — /gozem-money-user-services/new
                // (read-only until authoring endpoints ship on
                // @gozem-api/gozem-money-user-service).
                path: "gozem-money-user-services/new",
                canActivate: [permissionGuard("gozem_money_user_services:create")],
                loadComponent: () =>
                    import("./pages/gozem-money-user-services-form/gozem-money-user-services-form").then(
                        (m) => m.GozemMoneyUserServicesFormPage
                    )
            },
            {
                // Lows-links — legacy « Voir »: the service form read-only, for :view holders.
                path: "gozem-money-user-services/:id/view",
                canActivate: [permissionGuard("gozem_money_user_services:view")],
                data: {readOnly: true},
                loadComponent: () =>
                    import("./pages/gozem-money-user-services-form/gozem-money-user-services-form").then(
                        (m) => m.GozemMoneyUserServicesFormPage
                    )
            },
            {
                // Phase 1 scaffold — /gozem-money-user-services/:id
                // (read-only detail view + legacy deep-link for edit).
                path: "gozem-money-user-services/:id",
                canActivate: [permissionGuard("gozem_money_user_services:update")],
                loadComponent: () =>
                    import("./pages/gozem-money-user-services-form/gozem-money-user-services-form").then(
                        (m) => m.GozemMoneyUserServicesFormPage
                    )
            },
            {
                // Mop-up #11 — list-only v2 of legacy /gozem_money_merchant_services.
                path: "gozem-money-merchant-services",
                canActivate: [permissionGuard("gozem_money_merchant_services:list")],
                loadComponent: () =>
                    import("./pages/gozem-money-merchant-services-list/gozem-money-merchant-services-list").then(
                        (m) => m.GozemMoneyMerchantServicesListPage
                    )
            },
            {
                // Phase 1 scaffold — /gozem-money-merchant-services/new
                // (read-only until authoring endpoints ship on
                // @gozem-api/gozem-money-merchant-service).
                path: "gozem-money-merchant-services/new",
                canActivate: [permissionGuard("gozem_money_merchant_services:create")],
                loadComponent: () =>
                    import("./pages/gozem-money-merchant-services-form/gozem-money-merchant-services-form").then(
                        (m) => m.GozemMoneyMerchantServicesFormPage
                    )
            },
            {
                // Lows-links — legacy « Voir »: the service form read-only, for :view holders.
                path: "gozem-money-merchant-services/:id/view",
                canActivate: [permissionGuard("gozem_money_merchant_services:view")],
                data: {readOnly: true},
                loadComponent: () =>
                    import("./pages/gozem-money-merchant-services-form/gozem-money-merchant-services-form").then(
                        (m) => m.GozemMoneyMerchantServicesFormPage
                    )
            },
            {
                // Phase 1 scaffold — /gozem-money-merchant-services/:id
                // (read-only detail view + legacy deep-link for edit).
                path: "gozem-money-merchant-services/:id",
                canActivate: [permissionGuard("gozem_money_merchant_services:update")],
                loadComponent: () =>
                    import("./pages/gozem-money-merchant-services-form/gozem-money-merchant-services-form").then(
                        (m) => m.GozemMoneyMerchantServicesFormPage
                    )
            },
            {
                // Mop-up #11 — list-only v2 of legacy /gozem_money_champion_services.
                path: "gozem-money-champion-services",
                canActivate: [permissionGuard("gozem_money_champion_services:list")],
                loadComponent: () =>
                    import("./pages/gozem-money-champion-services-list/gozem-money-champion-services-list").then(
                        (m) => m.GozemMoneyChampionServicesListPage
                    )
            },
            {
                // Phase 1 scaffold — /gozem-money-champion-services/new
                // (read-only until authoring endpoints ship on
                // @gozem-api/gozem-money-champion-service).
                path: "gozem-money-champion-services/new",
                canActivate: [permissionGuard("gozem_money_champion_services:create")],
                loadComponent: () =>
                    import("./pages/gozem-money-champion-services-form/gozem-money-champion-services-form").then(
                        (m) => m.GozemMoneyChampionServicesFormPage
                    )
            },
            {
                // Lows-links — legacy « Voir »: the service form read-only, for :view holders.
                path: "gozem-money-champion-services/:id/view",
                canActivate: [permissionGuard("gozem_money_champion_services:view")],
                data: {readOnly: true},
                loadComponent: () =>
                    import("./pages/gozem-money-champion-services-form/gozem-money-champion-services-form").then(
                        (m) => m.GozemMoneyChampionServicesFormPage
                    )
            },
            {
                // Phase 1 scaffold — /gozem-money-champion-services/:id
                // (read-only detail view + legacy deep-link for edit).
                path: "gozem-money-champion-services/:id",
                canActivate: [permissionGuard("gozem_money_champion_services:update")],
                loadComponent: () =>
                    import("./pages/gozem-money-champion-services-form/gozem-money-champion-services-form").then(
                        (m) => m.GozemMoneyChampionServicesFormPage
                    )
            },
            {
                // Giant #7 — v2 of legacy /orders (main orders
                // flow only: user_type !== DRIVER, order_type !== 'adv',
                // is_digital !== true; row actions native).
                path: "orders",
                canActivate: [permissionGuard("orders:list")],
                loadComponent: () => import("./pages/orders-list/orders-list").then((m) => m.OrdersListPage)
            },
            {
                // Giant #8 — FINAL — v2 of legacy /trips
                // (154 columns full parity; row actions native).
                path: "trips",
                canActivate: [permissionGuard("trips:list")],
                loadComponent: () => import("./pages/trips-list/trips-list").then((m) => m.TripsListPage)
            },
            {
                // Wave K7-TripMap (2026-09-15) — read-only visualisation
                // of a single trip (Google-Maps trace equivalent).
                // Backed by GET /trips/:id/tracking on @gozem-api/trip.
                // Retires the submitLegacyForm shim on
                // trip-pauses-list.ts L407-410 (row action
                // "trip-details" → legacy /request_send_driver/trip_map).
                path: "trips/:id/map",
                canActivate: [permissionGuard("trips:view_tracking")],
                loadComponent: () => import("./pages/trip-map/trip-map").then((m) => m.TripMapPage)
            },
            {
                // Giant #2 — v2 of legacy /drivers (every row action native).
                path: "drivers",
                canActivate: [permissionGuard("drivers:list")],
                loadComponent: () => import("./pages/drivers-list/drivers-list").then((m) => m.DriversListPage)
            },
            // Legacy drivers.html:2227-2233 « Décliner » — the champion termination wizard (decline_champion.html).
            {
                path: "drivers/:id/termination",
                canActivate: [permissionGuard("drivers:update")],
                loadComponent: () =>
                    import("./pages/champion-termination/champion-termination").then((m) => m.ChampionTerminationPage)
            },
            // Legacy drivers.html:2020-2028 « Voir le détail » — the champion read-only (drivers:view).
            {
                path: "drivers/:id/detail",
                canActivate: [permissionGuard("drivers:view")],
                loadComponent: () => import("./pages/driver-detail/driver-detail").then((m) => m.DriverDetailPage)
            },
            {
                // Batch 8b (GPGT-39279) — legacy driver_modal migration.
                // Dedicated form route that replaces the in-place edit
                // modal on the drivers list (drivers.html L2337-L2562).
                // Create mode is NOT exposed: champions are provisioned
                // via the mobile onboarding wizard, never by an admin
                // typing them in.
                path: "drivers/:id",
                canActivate: [permissionGuard("drivers:update")],
                loadComponent: () => import("./pages/drivers-form/drivers-form").then((m) => m.DriversFormPage)
            },
            {
                // Giant #3 — v2 of legacy /riders (row actions native).
                path: "riders",
                canActivate: [permissionGuard("riders:list")],
                loadComponent: () => import("./pages/riders-list/riders-list").then((m) => m.RidersListPage)
            },
            {
                path: "digital-orders",
                canActivate: [permissionGuard("digital_orders:list")],
                loadComponent: () =>
                    import("./pages/digital-orders-list/digital-orders-list").then((m) => m.DigitalOrdersListPage)
            },
            {
                path: "dtone-orders",
                canActivate: [permissionGuard("dtone_orders:list")],
                loadComponent: () =>
                    import("./pages/dtone-orders-list/dtone-orders-list").then((m) => m.DtoneOrdersListPage)
            },
            {
                path: "order-acceptance-managed-by-gozem",
                canActivate: [permissionGuard("order_acceptance_managed_by_gozem:list")],
                loadComponent: () =>
                    import(
                        "./pages/order-acceptance-managed-by-gozem-list/order-acceptance-managed-by-gozem-list"
                    ).then((m) => m.OrderAcceptanceManagedByGozemListPage)
            },
            {
                path: "gozem-messaging-sms-config",
                canActivate: [permissionGuard("gozem_messaging_sms_config:list")],
                loadComponent: () =>
                    import("./pages/gozem-messaging-sms-config-list/gozem-messaging-sms-config-list").then(
                        (m) => m.GozemMessagingSmsConfigListPage
                    )
            },
            {
                path: "gozem-messaging-logs",
                canActivate: [permissionGuard("gozem_messaging_logs:list")],
                loadComponent: () =>
                    import("./pages/gozem-messaging-logs-list/gozem-messaging-logs-list").then(
                        (m) => m.GozemMessagingLogsListPage
                    )
            },
            {
                path: "gozem-messaging-transactional-logs",
                canActivate: [permissionGuard("gozem_messaging_transactional_logs:list")],
                loadComponent: () =>
                    import(
                        "./pages/gozem-messaging-transactional-logs-list/gozem-messaging-transactional-logs-list"
                    ).then((m) => m.GozemMessagingTransactionalLogsListPage)
            },
            {
                path: "asset-tracking",
                canActivate: [permissionGuard("asset_tracking:list")],
                loadComponent: () =>
                    import("./pages/asset-tracking-list/asset-tracking-list").then((m) => m.AssetTrackingListPage)
            },
            {
                path: "branch-install-logs",
                canActivate: [permissionGuard("branch_install_logs:list")],
                loadComponent: () =>
                    import("./pages/branch-install-logs-list/branch-install-logs-list").then(
                        (m) => m.BranchInstallLogsListPage
                    )
            },
            {
                path: "services-validation-setting",
                canActivate: [permissionGuard("services_validation_settings:list")],
                loadComponent: () =>
                    import("./pages/services-validation-setting-list/services-validation-setting-list").then(
                        (m) => m.ServicesValidationSettingListPage
                    )
            },
            {
                path: "gozem-messaging-clients",
                canActivate: [permissionGuard("gozem_messaging_clients:list")],
                loadComponent: () =>
                    import("./pages/gozem-messaging-clients-list/gozem-messaging-clients-list").then(
                        (m) => m.GozemMessagingClientsListPage
                    )
            },
            {
                path: "stock-location",
                canActivate: [permissionGuard("stock_locations:list")],
                loadComponent: () =>
                    import("./pages/stock-location-list/stock-location-list").then((m) => m.StockLocationListPage)
            },
            {
                path: "product-skus",
                canActivate: [permissionGuard("product_skus:list")],
                loadComponent: () =>
                    import("./pages/product-skus-list/product-skus-list").then((m) => m.ProductSkusListPage)
            },
            {
                path: "product-skus/new",
                canActivate: [permissionGuard("product_skus:create")],
                loadComponent: () =>
                    import("./pages/product-skus-form/product-skus-form").then((m) => m.ProductSkusFormPage)
            },
            {
                path: "product-skus/:id",
                canActivate: [permissionGuard("product_skus:update")],
                loadComponent: () =>
                    import("./pages/product-skus-form/product-skus-form").then((m) => m.ProductSkusFormPage)
            },
            {
                path: "stock-reports",
                canActivate: [permissionGuard("stock_reports:list")],
                loadComponent: () =>
                    import("./pages/stock-reports-list/stock-reports-list").then((m) => m.StockReportsListPage)
            },
            {
                path: "gowin-subscription-reminder-config",
                canActivate: [permissionGuard("gowin_subscription_reminder_config:list")],
                loadComponent: () =>
                    import("./pages/gowin-subscription-reminder-config/gowin-subscription-reminder-config").then(
                        (m) => m.GowinSubscriptionReminderConfigPage
                    )
            },
            // ── Xero admin batch (June 2026) ──
            // admin-xero-reports + admin-xero-settings. Both pages talk to
            // the existing @gozem-api/xero module (live Xero OAuth flow +
            // persisted trial-balance ledger). Modal-CRUD on settings;
            // generate-report modal on reports. Bespoke services (not
            // CrudService) because the URL layout splits between plural
            // /xero/accounts and singular /xero/account/:id.
            {
                path: "admin-xero-reports",
                canActivate: [permissionGuard("admin_xero_reports:list")],
                loadComponent: () =>
                    import("./pages/admin-xero-reports-list/admin-xero-reports-list").then(
                        (m) => m.AdminXeroReportsListPage
                    )
            },
            {
                path: "admin-xero-settings",
                canActivate: [permissionGuard("admin_xero_settings:list")],
                loadComponent: () =>
                    import("./pages/admin-xero-settings-list/admin-xero-settings-list").then(
                        (m) => m.AdminXeroSettingsListPage
                    )
            },
            {
                path: "web-landing-page-templates",
                canActivate: [permissionGuard("web_landing_page_templates:list")],
                loadComponent: () =>
                    import("./pages/web-landing-page-templates-list/web-landing-page-templates-list").then(
                        (m) => m.WebLandingPageTemplatesListPage
                    )
            },
            {
                path: "sku-product-validation",
                canActivate: [permissionGuard("sku_product_validation:list")],
                loadComponent: () =>
                    import("./pages/sku-product-validation-list/sku-product-validation-list").then(
                        (m) => m.SkuProductValidationListPage
                    )
            },
            {
                path: "sku-product-validation-log",
                canActivate: [permissionGuard("sku_product_validation_log:list")],
                loadComponent: () =>
                    import("./pages/sku-product-validation-log-list/sku-product-validation-log-list").then(
                        (m) => m.SkuProductValidationLogListPage
                    )
            },
            {
                path: "sku-product-solds",
                canActivate: [permissionGuard("sku_product_solds:list")],
                loadComponent: () =>
                    import("./pages/sku-product-solds-list/sku-product-solds-list").then(
                        (m) => m.SkuProductSoldsListPage
                    )
            },
            {
                path: "condition-reports",
                canActivate: [permissionGuard("condition_reports:list")],
                loadComponent: () =>
                    import("./pages/condition-reports-list/condition-reports-list").then(
                        (m) => m.ConditionReportsListPage
                    )
            },
            {
                path: "movement-reports",
                canActivate: [permissionGuard("movement_reports:list")],
                loadComponent: () =>
                    import("./pages/movement-reports-list/movement-reports-list").then((m) => m.MovementReportsListPage)
            },
            {
                path: "minimum-activity",
                canActivate: [permissionGuard("minimum_activity:list")],
                loadComponent: () =>
                    import("./pages/minimum-activity-page/minimum-activity-page").then((m) => m.MinimumActivityPage)
            },
            {
                path: "minimum-activity-data",
                canActivate: [permissionGuard("minimum_activity_data:list")],
                loadComponent: () =>
                    import("./pages/minimum-activity-data-list/minimum-activity-data-list").then(
                        (m) => m.MinimumActivityDataListPage
                    )
            },
            {
                path: "leader-board",
                canActivate: [permissionGuard("leader_board:list")],
                loadComponent: () =>
                    import("./pages/leader-board-list/leader-board-list").then((m) => m.LeaderBoardListPage)
            },
            {
                path: "hall-of-fame",
                canActivate: [permissionGuard("hall_of_fame:list")],
                loadComponent: () =>
                    import("./pages/hall-of-fame-list/hall-of-fame-list").then((m) => m.HallOfFameListPage)
            },
            {
                path: "processor",
                canActivate: [permissionGuard("processor:list")],
                loadComponent: () => import("./pages/processor-list/processor-list").then((m) => m.ProcessorListPage)
            },
            {
                path: "processors/new",
                canActivate: [permissionGuard("processor:create")],
                loadComponent: () => import("./pages/processor-form/processor-form").then((m) => m.ProcessorFormPage)
            },
            {
                path: "processors/:id",
                canActivate: [permissionGuard("processor:update")],
                loadComponent: () => import("./pages/processor-form/processor-form").then((m) => m.ProcessorFormPage)
            },
            {
                path: "option-categories",
                canActivate: [permissionGuard("option_categories:list")],
                loadComponent: () =>
                    import("./pages/option-categories-list/option-categories-list").then(
                        (m) => m.OptionCategoriesListPage
                    )
            },
            {
                path: "categories",
                canActivate: [permissionGuard("categories:list")],
                loadComponent: () => import("./pages/categories-list/categories-list").then((m) => m.CategoriesListPage)
            },
            {
                path: "checkouts",
                canActivate: [permissionGuard("checkouts:list")],
                loadComponent: () => import("./pages/checkouts-list/checkouts-list").then((m) => m.CheckoutsListPage)
            },
            {
                path: "order-auto-processings",
                canActivate: [permissionGuard("order_auto_processings:list")],
                loadComponent: () =>
                    import("./pages/order-auto-processings-list/order-auto-processings-list").then(
                        (m) => m.OrderAutoProcessingsListPage
                    )
            },
            {
                path: "merchant-pauses",
                canActivate: [permissionGuard("merchant_pauses:list")],
                loadComponent: () =>
                    import("./pages/merchant-pauses-list/merchant-pauses-list").then((m) => m.MerchantPausesListPage)
            },
            {
                path: "merchant-curation-sections",
                canActivate: [permissionGuard("merchant_curation_sections:list")],
                loadComponent: () =>
                    import("./pages/merchant-curation-sections-list/merchant-curation-sections-list").then(
                        (m) => m.MerchantCurationSectionsListPage
                    )
            },
            {
                path: "merchant-curation-sections/new",
                canActivate: [permissionGuard("merchant_curation_sections:create")],
                loadComponent: () =>
                    import("./pages/merchant-curation-sections-form/merchant-curation-sections-form").then(
                        (m) => m.MerchantCurationSectionsFormPage
                    )
            },
            {
                path: "merchant-curation-sections/:id",
                canActivate: [permissionGuard("merchant_curation_sections:update")],
                loadComponent: () =>
                    import("./pages/merchant-curation-sections-form/merchant-curation-sections-form").then(
                        (m) => m.MerchantCurationSectionsFormPage
                    )
            },
            {
                path: "merchant-fleet",
                canActivate: [permissionGuard("merchant_fleet:list")],
                loadComponent: () =>
                    import("./pages/merchant-fleet-list/merchant-fleet-list").then((m) => m.MerchantFleetListPage)
            },
            {
                path: "merchant-users",
                canActivate: [permissionGuard("merchant_users:list")],
                loadComponent: () =>
                    import("./pages/merchant-users-list/merchant-users-list").then((m) => m.MerchantUsersListPage)
            },
            {
                path: "leads",
                canActivate: [permissionGuard("leads:list")],
                loadComponent: () => import("./pages/leads-list/leads-list").then((m) => m.LeadsListPage)
            },
            {
                path: "uncollected-financing-contracts",
                canActivate: [permissionGuard("uncollected_financing_contracts:list")],
                loadComponent: () =>
                    import("./pages/uncollected-financing-contracts-list/uncollected-financing-contracts-list").then(
                        (m) => m.UncollectedFinancingContractsListPage
                    )
            },
            {
                path: "financing-templates",
                canActivate: [permissionGuard("financing_templates:list")],
                loadComponent: () =>
                    import("./pages/financing-templates-list/financing-templates-list").then(
                        (m) => m.FinancingTemplatesListPage
                    )
            },
            {
                path: "financing-templates/new",
                canActivate: [permissionGuard("financing_templates:create")],
                loadComponent: () =>
                    import("./pages/financing-templates-form/financing-templates-form").then(
                        (m) => m.FinancingTemplatesFormPage
                    )
            },
            {
                path: "financing-templates/:id",
                canActivate: [permissionGuard("financing_templates:update")],
                loadComponent: () =>
                    import("./pages/financing-templates-form/financing-templates-form").then(
                        (m) => m.FinancingTemplatesFormPage
                    )
            },
            {
                path: "financing-providers",
                canActivate: [permissionGuard("financing_providers:list")],
                loadComponent: () =>
                    import("./pages/financing-providers-list/financing-providers-list").then(
                        (m) => m.FinancingProvidersListPage
                    )
            },
            {
                path: "financing-providers/new",
                canActivate: [permissionGuard("financing_providers:create")],
                loadComponent: () =>
                    import("./pages/financing-providers-form/financing-providers-form").then(
                        (m) => m.FinancingProvidersFormPage
                    )
            },
            {
                path: "financing-providers/:id",
                canActivate: [permissionGuard("financing_providers:update")],
                loadComponent: () =>
                    import("./pages/financing-providers-form/financing-providers-form").then(
                        (m) => m.FinancingProvidersFormPage
                    )
            },
            {
                path: "financing-contracts",
                canActivate: [permissionGuard("financing_contracts:list")],
                loadComponent: () =>
                    import("./pages/financing-contracts-list/financing-contracts-list").then(
                        (m) => m.FinancingContractsListPage
                    )
            },
            {
                // Wave K5-FUP1 — scaffold. Read-only per-record view backed
                // by GET /financing-contracts/:id (@gozem-api/financing-contract).
                // See pages/financing-contracts-form docblock for deferred
                // panels tracked as Wave K5-FUP2.
                path: "financing-contracts/:id",
                canActivate: [permissionGuard("financing_contracts:view")],
                loadComponent: () =>
                    import("./pages/financing-contracts-form/financing-contracts-form").then(
                        (m) => m.FinancingContractsFormPage
                    )
            },
            {
                path: "financing-payments",
                canActivate: [permissionGuard("financing_payments:list")],
                loadComponent: () =>
                    import("./pages/financing-payments-list/financing-payments-list").then(
                        (m) => m.FinancingPaymentsListPage
                    )
            },
            {
                path: "gowin-profiles",
                canActivate: [permissionGuard("gowin_profiles:list")],
                loadComponent: () =>
                    import("./pages/gowin-profiles-list/gowin-profiles-list").then((m) => m.GowinProfilesListPage)
            },
            {
                path: "gowin-transactions",
                canActivate: [permissionGuard("gowin_transactions:list")],
                loadComponent: () =>
                    import("./pages/gowin-transactions-list/gowin-transactions-list").then(
                        (m) => m.GowinTransactionsListPage
                    )
            },
            {
                path: "topup-withdrawal-methods",
                canActivate: [permissionGuard("topup_withdrawal_methods:list")],
                loadComponent: () =>
                    import("./pages/topup-withdrawal-methods-list/topup-withdrawal-methods-list").then(
                        (m) => m.TopupWithdrawalMethodsListPage
                    )
            },
            {
                path: "gozem-money-merchant-kyb",
                canActivate: [permissionGuard("gozem_money_merchant_kybs:list")],
                loadComponent: () =>
                    import("./pages/gozem-money-merchant-kyb-list/gozem-money-merchant-kyb-list").then(
                        (m) => m.GozemMoneyMerchantKybListPage
                    )
            },
            {
                path: "tasks",
                canActivate: [permissionGuard("tasks:list")],
                loadComponent: () => import("./pages/tasks-list/tasks-list").then((m) => m.TasksListPage)
            },
            {
                path: "tasks/new",
                canActivate: [permissionGuard("tasks:create")],
                loadComponent: () => import("./pages/tasks-form/tasks-form").then((m) => m.TasksFormPage)
            },
            {
                path: "tasks/:id",
                canActivate: [permissionGuard("tasks:update")],
                loadComponent: () => import("./pages/tasks-form/tasks-form").then((m) => m.TasksFormPage)
            },
            {
                path: "pipeline",
                canActivate: [permissionGuard("pipelines:list")],
                loadComponent: () => import("./pages/pipeline-list/pipeline-list").then((m) => m.PipelineListPage)
            },
            {
                path: "pipelines/new",
                canActivate: [permissionGuard("pipelines:create")],
                loadComponent: () => import("./pages/pipeline-form/pipeline-form").then((m) => m.PipelineFormPage)
            },
            {
                path: "pipelines/:id",
                canActivate: [permissionGuard("pipelines:update")],
                loadComponent: () => import("./pages/pipeline-form/pipeline-form").then((m) => m.PipelineFormPage)
            },
            {
                path: "task-status",
                canActivate: [permissionGuard("task_status:list")],
                loadComponent: () =>
                    import("./pages/task-status-list/task-status-list").then((m) => m.TaskStatusListPage)
            },
            {
                path: "task-triggers",
                canActivate: [permissionGuard("task_triggers:list")],
                loadComponent: () =>
                    import("./pages/task-triggers-list/task-triggers-list").then((m) => m.TaskTriggersListPage)
            },
            {
                path: "task-triggers/new",
                canActivate: [permissionGuard("task_triggers:create")],
                loadComponent: () =>
                    import("./pages/task-triggers-form/task-triggers-form").then((m) => m.TaskTriggersFormPage)
            },
            {
                path: "task-triggers/:id",
                canActivate: [permissionGuard("task_triggers:update")],
                loadComponent: () =>
                    import("./pages/task-triggers-form/task-triggers-form").then((m) => m.TaskTriggersFormPage)
            },
            {
                path: "corporate-users",
                canActivate: [permissionGuard("corporate_users:list")],
                loadComponent: () =>
                    import("./pages/corporate-users-list/corporate-users-list").then((m) => m.CorporateUsersListPage)
            },
            {
                path: "corporate-groups",
                canActivate: [permissionGuard("corporate_groups:list")],
                loadComponent: () =>
                    import("./pages/corporate-groups-list/corporate-groups-list").then((m) => m.CorporateGroupsListPage)
            },
            {
                path: "corporate-rules",
                canActivate: [permissionGuard("corporate_rules:list")],
                loadComponent: () =>
                    import("./pages/corporate-rules-list/corporate-rules-list").then((m) => m.CorporateRulesListPage)
            },
            {
                path: "dispatch-temporary-exclusions",
                canActivate: [permissionGuard("dispatch_temporary_exclusions:list")],
                loadComponent: () =>
                    import("./pages/dispatch-temporary-exclusions-list/dispatch-temporary-exclusions-list").then(
                        (m) => m.DispatchTemporaryExclusionsListPage
                    )
            },
            {
                path: "transfers",
                canActivate: [permissionGuard("transfers:list")],
                loadComponent: () => import("./pages/transfers-list/transfers-list").then((m) => m.TransfersListPage)
            },
            {
                path: "transfers/new",
                canActivate: [permissionGuard("transfers:create")],
                loadComponent: () => import("./pages/transfers-form/transfers-form").then((m) => m.TransfersFormPage)
            },
            {
                path: "transfers/:id",
                canActivate: [permissionGuard("transfers:update")],
                loadComponent: () => import("./pages/transfers-form/transfers-form").then((m) => m.TransfersFormPage)
            },
            {
                path: "disputes",
                canActivate: [permissionGuard("disputes:list")],
                loadComponent: () => import("./pages/disputes-list/disputes-list").then((m) => m.DisputesListPage)
            },
            {
                path: "cashflow-transactions",
                canActivate: [permissionGuard("cashflow_transactions:list")],
                loadComponent: () =>
                    import("./pages/cashflow-transactions-list/cashflow-transactions-list").then(
                        (m) => m.CashflowTransactionsListPage
                    )
            },
            {
                path: "payment-requests",
                canActivate: [permissionGuard("payment_requests:list")],
                loadComponent: () =>
                    import("./pages/payment-requests-list/payment-requests-list").then((m) => m.PaymentRequestsListPage)
            },
            {
                path: "referrals",
                canActivate: [permissionGuard("referrals:list")],
                loadComponent: () => import("./pages/referrals-list/referrals-list").then((m) => m.ReferralsListPage)
            },
            {
                path: "survey-responses",
                canActivate: [permissionGuard("survey_responses:list")],
                loadComponent: () =>
                    import("./pages/survey-responses-list/survey-responses-list").then((m) => m.SurveyResponsesListPage)
            },
            {
                path: "whatsapp-templates",
                canActivate: [permissionGuard("whatsapp_templates:list")],
                loadComponent: () =>
                    import("./pages/whatsapp-templates-list/whatsapp-templates-list").then(
                        (m) => m.WhatsappTemplatesListPage
                    )
            },
            {
                path: "survey-templates",
                canActivate: [permissionGuard("survey_templates:list")],
                loadComponent: () =>
                    import("./pages/survey-templates-list/survey-templates-list").then((m) => m.SurveyTemplatesListPage)
            },
            {
                path: "survey-templates/new",
                canActivate: [permissionGuard("survey_templates:create")],
                loadComponent: () =>
                    import("./pages/survey-templates-form/survey-templates-form").then((m) => m.SurveyTemplatesFormPage)
            },
            {
                // Lows-links — legacy survey_template.html:274 « Voir le détail » (view_survey(id, false)): read-only.
                path: "survey-templates/:id/view",
                canActivate: [permissionGuard("survey_templates:view")],
                data: {readOnly: true},
                loadComponent: () =>
                    import("./pages/survey-templates-form/survey-templates-form").then((m) => m.SurveyTemplatesFormPage)
            },
            {
                path: "survey-templates/:id",
                canActivate: [permissionGuard("survey_templates:update")],
                loadComponent: () =>
                    import("./pages/survey-templates-form/survey-templates-form").then((m) => m.SurveyTemplatesFormPage)
            },
            // ── Wallet batch — Transaction Use Cases (June 2026) ──
            // Modal-based CRUD against the existing @gozem-api/transaction-use-case
            // backend. Custom actions: change-status, set-default. Steps editor
            // is a JSON textarea (DESIGN DEBT — structured editor pending).
            {
                path: "transaction-use-cases",
                canActivate: [permissionGuard("transaction_use_cases:list")],
                loadComponent: () =>
                    import("./pages/transaction-use-cases-list/transaction-use-cases-list").then(
                        (m) => m.TransactionUseCasesListPage
                    )
            },
            // ── Wallet batch — Wallets / Transaction Validation / Payments ──
            // Read-only list pages backed by @gozem-api/wallet, @gozem-api/payment,
            // and @gozem-api/transaction-validation. Transaction-validation also
            // exposes approve/reject row actions wired against the dedicated
            // POST endpoints; the legacy wallet $inc side-effect is deliberately
            // NOT replicated server-side (DESIGN DEBT — see controller header).
            {
                path: "wallets",
                canActivate: [permissionGuard("wallets:list")],
                loadComponent: () => import("./pages/wallets-list/wallets-list").then((m) => m.WalletsListPage)
            },
            {
                path: "transaction-validation",
                canActivate: [permissionGuard("transaction_validation:list")],
                loadComponent: () =>
                    import("./pages/transaction-validation-list/transaction-validation-list").then(
                        (m) => m.TransactionValidationListPage
                    )
            },
            {
                path: "payments",
                canActivate: [permissionGuard("payments:list")],
                loadComponent: () => import("./pages/payments-list/payments-list").then((m) => m.PaymentsListPage)
            },
            {
                // Task #189 — v2 of the `payment_refund` sidebar entry
                // (main-layout.ts). No standalone legacy page — promotes
                // the CSV bulk-refund modal off the `/payments` toolbar
                // ("Refund Payments" button) into its own route.
                path: "payment-refunds",
                canActivate: [permissionGuard("payment_refunds:create")],
                loadComponent: () => import("./pages/payment-refunds/payment-refunds").then((m) => m.PaymentRefundsPage)
            },
            {
                path: "champion-stock-reports",
                canActivate: [permissionGuard("champion_stock_reports:list")],
                loadComponent: () =>
                    import("./pages/champion-stock-reports-list/champion-stock-reports-list").then(
                        (m) => m.ChampionStockReportsListPage
                    )
            },
            {
                path: "request-send-driver",
                canActivate: [permissionGuard("request_send_driver:list")],
                loadComponent: () =>
                    import("./pages/request-send-driver-list/request-send-driver-list").then(
                        (m) => m.RequestSendDriverListPage
                    )
            },
            {
                path: "provider-tracking",
                canActivate: [permissionGuard("provider_tracking:list")],
                loadComponent: () =>
                    import("./pages/provider-tracking-list/provider-tracking-list").then(
                        (m) => m.ProviderTrackingListPage
                    )
            },
            {
                path: "driver-activity",
                canActivate: [permissionGuard("driver_activity:list")],
                loadComponent: () =>
                    import("./pages/driver-activity-list/driver-activity-list").then((m) => m.DriverActivityListPage)
            },
            {
                path: "provider-pause-validation-settings",
                canActivate: [permissionGuard("provider_pause_validation_settings:list")],
                loadComponent: () =>
                    import("./pages/provider-pause-validation-settings/provider-pause-validation-settings").then(
                        (m) => m.ProviderPauseValidationSettingsPage
                    )
            },
            // ── Riders batch (June 2026) ──
            // user-app-sessions: rider app session log (campaign + device).
            // rider-temporary-block-logs: rider blocks (shared collection
            //   with driver temp-block, filtered via ?user_type=rider).
            // address-books: rider address book entries + sharing graph.
            // dispatch-riders-soft-bans: rider soft-ban (dispatch only,
            //   with expiry + lead trips).
            {
                path: "user-app-sessions",
                canActivate: [permissionGuard("user_app_sessions:list")],
                loadComponent: () =>
                    import("./pages/user-app-sessions-list/user-app-sessions-list").then(
                        (m) => m.UserAppSessionsListPage
                    )
            },
            {
                path: "rider-temporary-block-logs",
                canActivate: [permissionGuard("rider_temporary_block_logs:list")],
                loadComponent: () =>
                    import("./pages/rider-temporary-block-logs-list/rider-temporary-block-logs-list").then(
                        (m) => m.RiderTemporaryBlockLogsListPage
                    )
            },
            {
                path: "address-books",
                canActivate: [permissionGuard("address_books:list")],
                loadComponent: () =>
                    import("./pages/address-books-list/address-books-list").then((m) => m.AddressBooksListPage)
            },
            {
                path: "dispatch-riders-soft-bans",
                canActivate: [permissionGuard("dispatch_riders_soft_bans:list")],
                loadComponent: () =>
                    import("./pages/dispatch-riders-soft-bans-list/dispatch-riders-soft-bans-list").then(
                        (m) => m.DispatchRidersSoftBansListPage
                    )
            },
            // ── Transport batch (June 2026) ──
            // Read-only listings for the four transport pages: calls,
            // chats (legacy chat_session admin log — NOT the feature-chat
            // product), support-activities (admin notes on trips), schedules
            // (scheduled_trip rows). Mirrors legacy calls.html, chats.html,
            // support_activities.html, schedules_list.html.
            {
                path: "calls",
                canActivate: [permissionGuard("calls:list")],
                loadComponent: () => import("./pages/calls-list/calls-list").then((m) => m.CallsListPage)
            },
            {
                path: "chats",
                canActivate: [permissionGuard("chats:list")],
                loadComponent: () => import("./pages/chats-list/chats-list").then((m) => m.ChatsListPage)
            },
            {
                path: "support-activities",
                canActivate: [permissionGuard("support_activities:list")],
                loadComponent: () =>
                    import("./pages/support-activities-list/support-activities-list").then(
                        (m) => m.SupportActivitiesListPage
                    )
            },
            {
                path: "schedules",
                canActivate: [permissionGuard("schedules:list")],
                loadComponent: () => import("./pages/schedules-list/schedules-list").then((m) => m.SchedulesListPage)
            },
            // Transport batch (continued) — trip-pause + vehicle-service-groups
            // + pelias-log list pages. Form view for vehicle-service-groups
            // is NOT migrated yet — actions punt to the legacy admin.
            {
                path: "trip-pauses",
                canActivate: [permissionGuard("trip_pauses:list")],
                loadComponent: () =>
                    import("./pages/trip-pauses-list/trip-pauses-list").then((m) => m.TripPausesListPage)
            },
            {
                path: "vehicle-service-groups",
                canActivate: [permissionGuard("vehicle_service_groups:list")],
                loadComponent: () =>
                    import("./pages/vehicle-service-groups-list/vehicle-service-groups-list").then(
                        (m) => m.VehicleServiceGroupsListPage
                    )
            },
            {
                path: "vehicle-service-groups/new",
                canActivate: [permissionGuard("vehicle_service_groups:create")],
                loadComponent: () =>
                    import("./pages/vehicle-service-groups-form/vehicle-service-groups-form").then(
                        (m) => m.VehicleServiceGroupsFormPage
                    )
            },
            {
                // Lows-links — legacy « Voir » on a deleted group: the form read-only.
                path: "vehicle-service-groups/:id/view",
                canActivate: [permissionGuard("vehicle_service_groups:view")],
                data: {readOnly: true},
                loadComponent: () =>
                    import("./pages/vehicle-service-groups-form/vehicle-service-groups-form").then(
                        (m) => m.VehicleServiceGroupsFormPage
                    )
            },
            {
                path: "vehicle-service-groups/:id",
                canActivate: [permissionGuard("vehicle_service_groups:update")],
                loadComponent: () =>
                    import("./pages/vehicle-service-groups-form/vehicle-service-groups-form").then(
                        (m) => m.VehicleServiceGroupsFormPage
                    )
            },
            {
                path: "pelias-logs",
                canActivate: [permissionGuard("pelias_logs:list")],
                loadComponent: () =>
                    import("./pages/pelias-logs-list/pelias-logs-list").then((m) => m.PeliasLogsListPage)
            },
            // ── Bonus programs batch (June 2026) ──
            // List + dedicated form (4-card editor: identification /
            // audience / periodicity / incentives — with signal-backed
            // incentives table for the KPI → reward rules). Status-flip
            // shortcuts call dedicated PUT /:id/activate /:id/deactivate.
            {
                path: "bonus-programs",
                canActivate: [permissionGuard("bonus_programs:list")],
                loadComponent: () =>
                    import("./pages/bonus-programs-list/bonus-programs-list").then((m) => m.BonusProgramsListPage)
            },
            {
                path: "bonus-programs/new",
                canActivate: [permissionGuard("bonus_programs:create")],
                loadComponent: () =>
                    import("./pages/bonus-programs-form/bonus-programs-form").then((m) => m.BonusProgramsFormPage)
            },
            {
                path: "bonus-programs/:id",
                canActivate: [permissionGuard("bonus_programs:update")],
                loadComponent: () =>
                    import("./pages/bonus-programs-form/bonus-programs-form").then((m) => m.BonusProgramsFormPage)
            },
            // ── App visuals + SMS batch (June 2026) ──
            // 2 small pages. App-visuals = modal CRUD with bilingual
            // display_settings + image upload (FileReader → base64).
            // SMS-templates = selector form (same shape as
            // email-templates, mirrors the 33-LOC legacy sms.html).
            // The segment domain was descoped — it's a 50-field
            // audience-builder with advanced query, deserves its own batch.
            {
                path: "app-visuals",
                canActivate: [permissionGuard("app_visuals:list")],
                loadComponent: () =>
                    import("./pages/app-visuals-list/app-visuals-list").then((m) => m.AppVisualsListPage)
            },
            {
                path: "sms-templates",
                canActivate: [permissionGuard("sms:list")],
                loadComponent: () =>
                    import("./pages/sms-templates-list/sms-templates-list").then((m) => m.SmsTemplatesListPage)
            },
            // ── Orphan backends batch 2 (June 2026) ──
            // Three more @gozem-api/* modules with backends ready, no v2
            // page yet: gowin-products (read-only — creation builder is
            // complex), driver-cards (full CRUD, daily admin workflow),
            // automated-payments (read-only — approval workflow + deep
            // editor deferred).
            {
                path: "gowin-products",
                canActivate: [permissionGuard("gowin_products:list")],
                loadComponent: () =>
                    import("./pages/gowin-products-list/gowin-products-list").then((m) => m.GowinProductsListPage)
            },
            // Round 72+ (gowin-products-form v2 migration) — dedicated
            // create/edit route mirroring legacy
            // `gowin_product_form.html` (345 LOC, 35 name= attrs). See
            // gowin-products-form.ts docblock for the card / field
            // inventory and the design-debt list.
            {
                path: "gowin-products/new",
                loadComponent: () =>
                    import("./pages/gowin-products-form/gowin-products-form").then((m) => m.GowinProductsFormPage),
                canActivate: [permissionGuard("gowin_products:create")]
            },
            {
                path: "gowin-products/:id",
                loadComponent: () =>
                    import("./pages/gowin-products-form/gowin-products-form").then((m) => m.GowinProductsFormPage),
                canActivate: [permissionGuard("gowin_products:update")]
            },
            {
                path: "driver-cards",
                canActivate: [permissionGuard("driver_cards:list")],
                loadComponent: () =>
                    import("./pages/driver-cards-list/driver-cards-list").then((m) => m.DriverCardsListPage)
            },
            {
                path: "automated-payments",
                canActivate: [permissionGuard("automated_payments:list")],
                loadComponent: () =>
                    import("./pages/automated-payments-list/automated-payments-list").then(
                        (m) => m.AutomatedPaymentsListPage
                    )
            },
            // ── Orphan backends batch (June 2026) ──
            // Five @gozem-api/* modules that had backends shipped but no
            // v2 page yet: full CRUD on the two simple ones, read-only
            // lists for the three richer ones (deep edit modals
            // deferred). Backends were already in the orchestrator's
            // dependency list — only frontend work in this batch.
            {
                path: "margin-templates",
                canActivate: [permissionGuard("margin_templates:list")],
                loadComponent: () =>
                    import("./pages/margin-templates-list/margin-templates-list").then((m) => m.MarginTemplatesListPage)
            },
            {
                path: "sku-model-tag-tva",
                canActivate: [permissionGuard("sku_model_tag_tva:list")],
                loadComponent: () =>
                    import("./pages/sku-model-tag-tva-list/sku-model-tag-tva-list").then(
                        (m) => m.SkuModelTagTvaListPage
                    )
            },
            {
                path: "quotations",
                canActivate: [permissionGuard("quotations:list")],
                loadComponent: () => import("./pages/quotations-list/quotations-list").then((m) => m.QuotationsListPage)
            },
            // Wave K6-Quotations — dedicated per-quote form. Mounted
            // at /quotations/:id. Ships the frame + POST /quotations/
            // :id/submit wiring. Card 4 (items[] dynamic array) is
            // DEFERRED — needs gz-file-upload-multi. See pages/
            // quotations-form docblock for the scoped follow-up plan.
            {
                path: "quotations/:id",
                canActivate: [permissionGuard("quotations:update")],
                loadComponent: () => import("./pages/quotations-form/quotations-form").then((m) => m.QuotationsFormPage)
            },
            {
                path: "purchases",
                canActivate: [permissionGuard("purchases:list")],
                loadComponent: () => import("./pages/purchases-list/purchases-list").then((m) => m.PurchasesListPage)
            },
            {
                path: "purchases/new",
                canActivate: [permissionGuard("purchases:create")],
                loadComponent: () => import("./pages/purchases-form/purchases-form").then((m) => m.PurchasesFormPage)
            },
            {
                path: "purchases/:id",
                canActivate: [permissionGuard("purchases:update")],
                loadComponent: () => import("./pages/purchases-form/purchases-form").then((m) => m.PurchasesFormPage)
            },
            {
                path: "segments",
                canActivate: [permissionGuard("segment:list")],
                loadComponent: () => import("./pages/segments-list/segments-list").then((m) => m.SegmentsListPage)
            },
            // Segments editor — Phase 1 scaffold (task #540). Mirrors
            // segment.html (1,082 LOC) modal-turned-page. Same component
            // handles both /new (create) and /:id (edit); the paramMap
            // decides. Cards 3-5 are DESIGN DEBT placeholders — the
            // criteria-editor grid arrives in Phase 2.
            {
                path: "segments/new",
                canActivate: [permissionGuard("segment:create")],
                loadComponent: () => import("./pages/segments-form/segments-form").then((m) => m.SegmentsFormPage)
            },
            {
                path: "segments/:id",
                canActivate: [permissionGuard("segment:update")],
                loadComponent: () => import("./pages/segments-form/segments-form").then((m) => m.SegmentsFormPage)
            },
            // ── System settings batch (June 2026) ──
            // cron-configs + configurations — both have backends already
            // built and registered in the orchestrator
            // (`@gozem-api/cron-config`, `@gozem-api/config`); v2 just ships
            // the list pages on top.
            {
                path: "cron-configs",
                canActivate: [permissionGuard("cron_configs:list")],
                loadComponent: () =>
                    import("./pages/cron-configs-list/cron-configs-list").then((m) => m.CronConfigsListPage)
            },
            {
                path: "configurations",
                canActivate: [permissionGuard("configuration:list")],
                loadComponent: () =>
                    import("./pages/configurations-list/configurations-list").then((m) => m.ConfigurationsListPage)
            },
            // ── Messaging logs batch (June 2026) ──
            // SMS / Email / Push notification audit logs. Same generic-with-
            // type-discriminator pattern as partner-logs — single page, three
            // routes, three different column projections (driven by
            // MESSAGING_LOG_REGISTRY in messaging-log.model.ts).
            //
            // Guarded on `messaging_logs:list` because that is what
            // `GET /messaging-logs` enforces, for every type: the per-type
            // `sms_logs` / `email_logs` / `push_notification_logs` tokens are
            // enforced by no endpoint, so a guard on them let an admin in to a
            // table that 401'd. See playwright/handover/wave3/page-resource.md.
            {
                path: "sms-logs",
                canActivate: [permissionGuard("messaging_logs:list")],
                data: {messagingLogType: "sms_log"},
                loadComponent: () =>
                    import("./pages/messaging-logs-list/messaging-logs-list").then((m) => m.MessagingLogsListPage)
            },
            {
                path: "email-logs",
                canActivate: [permissionGuard("messaging_logs:list")],
                data: {messagingLogType: "email_log"},
                loadComponent: () =>
                    import("./pages/messaging-logs-list/messaging-logs-list").then((m) => m.MessagingLogsListPage)
            },
            {
                path: "push-notification-logs",
                canActivate: [permissionGuard("messaging_logs:list")],
                data: {messagingLogType: "notification_log"},
                loadComponent: () =>
                    import("./pages/messaging-logs-list/messaging-logs-list").then((m) => m.MessagingLogsListPage)
            },
            // ── Partner integration logs batch (June 2026) ──
            // 16 partner-gateway log pages — every one shares the same
            // shape (`{unique_id, created_at, request, response}`), so the
            // backend collapses them into a single `@gozem-api/partner-log`
            // module with a `type` discriminator, and the frontend serves
            // every partner from one `PartnerLogsListPage` reading the
            // active partner from `data.partnerLogType`. Adding a new
            // partner = one route entry + one PARTNER_LOG_REGISTRY row.
            //
            // Guarded on `partner_logs:list` because that is what
            // `GET /partner-logs?type=…` enforces, whatever the type — the
            // per-partner `<partner>_log:list` tokens gate only the separate
            // `/logs/<partner>_logs` routes, which this page never calls.
            // See playwright/handover/wave3/page-resource.md.
            {
                path: "tmoney-log",
                canActivate: [permissionGuard("partner_logs:list")],
                data: {partnerLogType: "tmoney_log"},
                loadComponent: () =>
                    import("./pages/partner-logs-list/partner-logs-list").then((m) => m.PartnerLogsListPage)
            },
            {
                path: "flooz-log",
                canActivate: [permissionGuard("partner_logs:list")],
                data: {partnerLogType: "flooz_log"},
                loadComponent: () =>
                    import("./pages/partner-logs-list/partner-logs-list").then((m) => m.PartnerLogsListPage)
            },
            {
                path: "stripe-log",
                canActivate: [permissionGuard("partner_logs:list")],
                data: {partnerLogType: "stripe_log"},
                loadComponent: () =>
                    import("./pages/partner-logs-list/partner-logs-list").then((m) => m.PartnerLogsListPage)
            },
            {
                path: "twilio-log",
                canActivate: [permissionGuard("partner_logs:list")],
                data: {partnerLogType: "twilio_log"},
                loadComponent: () =>
                    import("./pages/partner-logs-list/partner-logs-list").then((m) => m.PartnerLogsListPage)
            },
            {
                path: "semoa-log",
                canActivate: [permissionGuard("partner_logs:list")],
                data: {partnerLogType: "semoa_log"},
                loadComponent: () =>
                    import("./pages/partner-logs-list/partner-logs-list").then((m) => m.PartnerLogsListPage)
            },
            {
                path: "semoa-pro-log",
                canActivate: [permissionGuard("partner_logs:list")],
                data: {partnerLogType: "semoa_pro_log"},
                loadComponent: () =>
                    import("./pages/partner-logs-list/partner-logs-list").then((m) => m.PartnerLogsListPage)
            },
            {
                path: "moov-benin-log",
                canActivate: [permissionGuard("partner_logs:list")],
                data: {partnerLogType: "moov_benin_log"},
                loadComponent: () =>
                    import("./pages/partner-logs-list/partner-logs-list").then((m) => m.PartnerLogsListPage)
            },
            {
                path: "mtn-log",
                canActivate: [permissionGuard("partner_logs:list")],
                data: {partnerLogType: "mtn_log"},
                loadComponent: () =>
                    import("./pages/partner-logs-list/partner-logs-list").then((m) => m.PartnerLogsListPage)
            },
            {
                path: "mtn-cm-log",
                canActivate: [permissionGuard("partner_logs:list")],
                data: {partnerLogType: "mtn_cm_log"},
                loadComponent: () =>
                    import("./pages/partner-logs-list/partner-logs-list").then((m) => m.PartnerLogsListPage)
            },
            {
                path: "airtel-gabon-log",
                canActivate: [permissionGuard("partner_logs:list")],
                data: {partnerLogType: "airtel_gabon_log"},
                loadComponent: () =>
                    import("./pages/partner-logs-list/partner-logs-list").then((m) => m.PartnerLogsListPage)
            },
            {
                path: "airtel-sms-log",
                canActivate: [permissionGuard("partner_logs:list")],
                data: {partnerLogType: "airtel_sms_log"},
                loadComponent: () =>
                    import("./pages/partner-logs-list/partner-logs-list").then((m) => m.PartnerLogsListPage)
            },
            {
                path: "gabon-sms-log",
                canActivate: [permissionGuard("partner_logs:list")],
                data: {partnerLogType: "gabon_sms_log"},
                loadComponent: () =>
                    import("./pages/partner-logs-list/partner-logs-list").then((m) => m.PartnerLogsListPage)
            },
            {
                path: "dial-sms-log",
                canActivate: [permissionGuard("partner_logs:list")],
                data: {partnerLogType: "dial_sms_log"},
                loadComponent: () =>
                    import("./pages/partner-logs-list/partner-logs-list").then((m) => m.PartnerLogsListPage)
            },
            {
                path: "nexah-sms-log",
                canActivate: [permissionGuard("partner_logs:list")],
                data: {partnerLogType: "nexah_sms_log"},
                loadComponent: () =>
                    import("./pages/partner-logs-list/partner-logs-list").then((m) => m.PartnerLogsListPage)
            },
            {
                path: "cam-pay-log",
                canActivate: [permissionGuard("partner_logs:list")],
                data: {partnerLogType: "cam_pay_log"},
                loadComponent: () =>
                    import("./pages/partner-logs-list/partner-logs-list").then((m) => m.PartnerLogsListPage)
            },
            {
                path: "mobi-cash-log",
                canActivate: [permissionGuard("partner_logs:list")],
                data: {partnerLogType: "mobi_cash_log"},
                loadComponent: () =>
                    import("./pages/partner-logs-list/partner-logs-list").then((m) => m.PartnerLogsListPage)
            },
            {
                // Legacy partner_logs.js partner_type 19 (Celtiis).
                path: "celtis-log",
                canActivate: [permissionGuard("partner_logs:list")],
                data: {partnerLogType: "celtis_log"},
                loadComponent: () =>
                    import("./pages/partner-logs-list/partner-logs-list").then((m) => m.PartnerLogsListPage)
            },
            {
                path: "trasolux-log",
                canActivate: [permissionGuard("partner_logs:list")],
                data: {partnerLogType: "trasolux_log"},
                loadComponent: () =>
                    import("./pages/partner-logs-list/partner-logs-list").then((m) => m.PartnerLogsListPage)
            },
            {
                path: "trasolux-log-partner",
                canActivate: [permissionGuard("partner_logs:list")],
                data: {partnerLogType: "trasolux_log"},
                loadComponent: () =>
                    import("./pages/partner-logs-list/partner-logs-list").then((m) => m.PartnerLogsListPage)
            },
            // ── Admin & access batch (June 2026) ──
            // admin-users + admin-groups. The groups page renders the full
            // permissions matrix via <app-permissions-matrix>; admin-users
            // wires the AdminGroupsService for the group multi-select.
            {
                path: "admin-users",
                canActivate: [permissionGuard("admin_users:list")],
                loadComponent: () =>
                    import("./pages/admin-users-list/admin-users-list").then((m) => m.AdminUsersListPage)
            },
            {
                // Self-service password change (GPGT-41206). Deliberately
                // NOT in the sidebar navTree — that mirrors the legacy
                // menu.json entry-for-entry and this is a v2-only,
                // account-scoped screen. Linked from the sidebar footer
                // beside "Se déconnecter". No permission guard: every
                // admin may change their OWN password, and the current
                // password is the proof.
                path: "change-password",
                loadComponent: () => import("./pages/change-password/change-password").then((m) => m.ChangePasswordPage)
            },
            {
                path: "admin-groups",
                canActivate: [permissionGuard("admin_groups:list")],
                loadComponent: () =>
                    import("./pages/admin-groups-list/admin-groups-list").then((m) => m.AdminGroupsListPage)
            },
            // ── Promo & pricing batch (June 2026) ──
            // promo-codes (list + dedicated form mirroring 890-LOC legacy)
            // + topup-settings, withdrawal-settings (one backend module,
            // two distinct resource paths) + payment-viewer-settings
            // (expense-dashboard scoping rules). Quotation deferred: no
            // legacy admin UI to mirror.
            {
                path: "promo-codes",
                canActivate: [permissionGuard("promo_codes:list")],
                loadComponent: () =>
                    import("./pages/promo-codes-list/promo-codes-list").then((m) => m.PromoCodesListPage)
            },
            {
                path: "promo-codes/new",
                canActivate: [permissionGuard("promo_codes:create")],
                loadComponent: () =>
                    import("./pages/promo-codes-form/promo-codes-form").then((m) => m.PromoCodesFormPage)
            },
            {
                path: "promo-codes/:id",
                canActivate: [permissionGuard("promo_codes:update")],
                loadComponent: () =>
                    import("./pages/promo-codes-form/promo-codes-form").then((m) => m.PromoCodesFormPage)
            },
            {
                path: "topup-settings",
                canActivate: [permissionGuard("top_up:list")],
                loadComponent: () =>
                    import("./pages/topup-settings-list/topup-settings-list").then((m) => m.TopupSettingsListPage)
            },
            {
                path: "withdrawal-settings",
                canActivate: [permissionGuard("withdrawal:list")],
                loadComponent: () =>
                    import("./pages/withdrawal-settings-list/withdrawal-settings-list").then(
                        (m) => m.WithdrawalSettingsListPage
                    )
            },
            {
                path: "payment-viewer-settings",
                canActivate: [permissionGuard("payment_viewer_settings:list")],
                loadComponent: () =>
                    import("./pages/payment-viewer-settings-list/payment-viewer-settings-list").then(
                        (m) => m.PaymentViewerSettingsListPage
                    )
            },
            // ── Self-inspection batch (June 2026) ──
            // control-templates (builder for inspection checklists, list +
            // dedicated form with dynamic element editor + recurring
            // schedule sub-editor) and control-instances (read-only
            // review queue with trigger modal + cancel/export actions).
            // Champion-control and assessor-review backends are wired
            // but deferred: champion endpoints are driver-app only, and
            // assessor review actions fold into the instance row actions
            // in a future iteration.
            {
                path: "control-templates",
                canActivate: [permissionGuard("control_templates:list")],
                loadComponent: () =>
                    import("./pages/control-templates-list/control-templates-list").then(
                        (m) => m.ControlTemplatesListPage
                    )
            },
            {
                path: "control-templates/new",
                canActivate: [permissionGuard("control_templates:create")],
                loadComponent: () =>
                    import("./pages/control-templates-form/control-templates-form").then(
                        (m) => m.ControlTemplatesFormPage
                    )
            },
            {
                path: "control-templates/:id",
                canActivate: [permissionGuard("control_templates:update")],
                loadComponent: () =>
                    import("./pages/control-templates-form/control-templates-form").then(
                        (m) => m.ControlTemplatesFormPage
                    )
            },
            {
                path: "control-instances",
                canActivate: [permissionGuard("control_instances:list")],
                loadComponent: () =>
                    import("./pages/control-instances-list/control-instances-list").then(
                        (m) => m.ControlInstancesListPage
                    )
            },
            // ── Expense management batch (June 2026) ──
            // expense-beneficiaries, beneficiary-external-accounts (bank +
            // mobile-money payout destinations), expense-categories
            // (hierarchical chart of accounts). expense-payment — Phase 1
            // form scaffold ships (Cards 1 + 2 = Payer + Beneficiary);
            // Cards 3-5 (Amounts / History / 3-stage Validation timeline)
            // are DESIGN DEBT pending validation-service + Gozem Money
            // proxies in @gozem-api/expense-payment. See docblock of
            // pages/expense-payments-form/expense-payments-form.ts.
            // Expense payments list (legacy expense_payment.html) — the page the
            // form's save and back land on. GET /expense-payments is gated on
            // expense_payments:list (pages/expense-payments-list).
            {
                path: "expense-payments",
                canActivate: [permissionGuard("expense_payments:list")],
                loadComponent: () =>
                    import("./pages/expense-payments-list/expense-payments-list").then((m) => m.ExpensePaymentsListPage)
            },
            {
                path: "expense-payments/new",
                canActivate: [permissionGuard("expense_payments:create")],
                loadComponent: () =>
                    import("./pages/expense-payments-form/expense-payments-form").then((m) => m.ExpensePaymentsFormPage)
            },
            {
                path: "expense-payments/:id",
                canActivate: [permissionGuard("expense_payments:update")],
                loadComponent: () =>
                    import("./pages/expense-payments-form/expense-payments-form").then((m) => m.ExpensePaymentsFormPage)
            },
            {
                path: "expense-beneficiaries",
                canActivate: [permissionGuard("expense_beneficiaries:list")],
                loadComponent: () =>
                    import("./pages/expense-beneficiaries-list/expense-beneficiaries-list").then(
                        (m) => m.ExpenseBeneficiariesListPage
                    )
            },
            {
                path: "beneficiary-external-accounts",
                canActivate: [permissionGuard("beneficiary_external_accounts:list")],
                loadComponent: () =>
                    import("./pages/beneficiary-external-accounts-list/beneficiary-external-accounts-list").then(
                        (m) => m.BeneficiaryExternalAccountsListPage
                    )
            },
            {
                path: "beneficiary-external-accounts/new",
                canActivate: [permissionGuard("beneficiary_external_accounts:create")],
                loadComponent: () =>
                    import(
                        "./pages/expense-beneficiary-external-accounts-form/expense-beneficiary-external-accounts-form"
                    ).then((m) => m.ExpenseBeneficiaryExternalAccountsFormPage)
            },
            {
                path: "beneficiary-external-accounts/:id",
                canActivate: [permissionGuard("beneficiary_external_accounts:update")],
                loadComponent: () =>
                    import(
                        "./pages/expense-beneficiary-external-accounts-form/expense-beneficiary-external-accounts-form"
                    ).then((m) => m.ExpenseBeneficiaryExternalAccountsFormPage)
            },
            {
                path: "expense-categories",
                canActivate: [permissionGuard("expense_categories:list")],
                loadComponent: () =>
                    import("./pages/expense-categories-list/expense-categories-list").then(
                        (m) => m.ExpenseCategoriesListPage
                    )
            },
            // Macro + Finance categories reuse the `expense_categories`
            // collection (type discriminator: "macro" / "finance"). Both
            // pages ship as modal-CRUD lists on top of the existing
            // @gozem-api/expense-category backend — no new module needed.
            // See macro-categories-list.ts + finance-categories-list.ts docblocks.
            {
                path: "macro-categories",
                canActivate: [permissionGuard("expense_categories:list")],
                loadComponent: () =>
                    import("./pages/macro-categories-list/macro-categories-list").then((m) => m.MacroCategoriesListPage)
            },
            {
                path: "finance-categories",
                canActivate: [permissionGuard("expense_categories:list")],
                loadComponent: () =>
                    import("./pages/finance-categories-list/finance-categories-list").then(
                        (m) => m.FinanceCategoriesListPage
                    )
            },
            // ── Ops monitoring trio (June 2026) ──
            // Three read-only filterable monitoring pages: cron-logs
            // (cron job audit trail), accident-logs (driver accident
            // reports with detail modal), bug-tickets (bug tracker with
            // scoring matrix). bug-tickets later grew a dedicated
            // `/bug-tickets/:id` read-only detail panel (mirrors legacy
            // `bug_details.html`) plus a separate `/bug-tickets/:id/edit`
            // form route. Create/edit for accident-logs stays deferred.
            {
                path: "cron-logs",
                canActivate: [permissionGuard("cron_log:list")],
                loadComponent: () => import("./pages/cron-logs-list/cron-logs-list").then((m) => m.CronLogsListPage)
            },
            {
                path: "accident-logs",
                canActivate: [permissionGuard("accident_logs:list")],
                loadComponent: () =>
                    import("./pages/accident-logs-list/accident-logs-list").then((m) => m.AccidentLogsListPage)
            },
            {
                path: "accident-logs/new",
                canActivate: [permissionGuard("accident_logs:create")],
                loadComponent: () =>
                    import("./pages/accident-logs-form/accident-logs-form").then((m) => m.AccidentLogsFormPage)
            },
            {
                path: "accident-logs/:id",
                canActivate: [permissionGuard("accident_logs:update")],
                loadComponent: () =>
                    import("./pages/accident-logs-form/accident-logs-form").then((m) => m.AccidentLogsFormPage)
            },
            {
                path: "bug-tickets",
                canActivate: [permissionGuard("bug_list:list")],
                loadComponent: () =>
                    import("./pages/bug-tickets-list/bug-tickets-list").then((m) => m.BugTicketsListPage)
            },
            {
                path: "bug-tickets/new",
                canActivate: [permissionGuard("bug_tickets:create")],
                loadComponent: () =>
                    import("./pages/bug-tickets-form/bug-tickets-form").then((m) => m.BugTicketsFormPage)
            },
            {
                path: "bug-tickets/:id/edit",
                canActivate: [permissionGuard("bug_tickets:update")],
                loadComponent: () =>
                    import("./pages/bug-tickets-form/bug-tickets-form").then((m) => m.BugTicketsFormPage)
            },
            // Read-only detail panel — mirrors legacy `/bug_details/:id`
            // (gozem-admin-web bug_details.html). Full two-column layout:
            // bug info + scoring/impact on the left, responsibles +
            // validation timeline on the right. Mutations happen via the
            // add-comment modal / dedicated edit route above, never inline.
            {
                path: "bug-tickets/:id",
                canActivate: [permissionGuard("bug_list:view")],
                loadComponent: () =>
                    import("./pages/bug-tickets-detail/bug-tickets-detail").then((m) => m.BugTicketsDetailPage)
            },
            // ── Driver lifecycle part 2 batch (June 2026) ──
            // blocking-rules (auto-sanction policy), job-positions
            // (HR/onboarding pipeline), immobilizations (asset
            // timeline read-only — the timeline-progress modal is
            // deferred). All 3 backends verified wired.
            {
                path: "blocking-rules",
                canActivate: [permissionGuard("blocking_rules:list")],
                loadComponent: () =>
                    import("./pages/blocking-rules-list/blocking-rules-list").then((m) => m.BlockingRulesListPage)
            },
            {
                path: "job-positions",
                canActivate: [permissionGuard("job_positions:list")],
                loadComponent: () =>
                    import("./pages/job-positions-list/job-positions-list").then((m) => m.JobPositionsListPage)
            },
            {
                path: "immobilizations",
                canActivate: [permissionGuard("immobilizations:list")],
                loadComponent: () =>
                    import("./pages/immobilizations-list/immobilizations-list").then((m) => m.ImmobilizationsListPage)
            },
            // ── Documents batch (June 2026) ──
            // documents = catalog of document types collected at
            // onboarding (CNI, driver license, vehicle registration,
            // …). Modal CRUD with conditional country picker based on
            // is_global.
            //
            // NB: onboarding-templates v2 page was reverted — the
            // `onboarding_template` collection exists and is wired in
            // the backend, but the legacy admin has NO management UI
            // (templates are seeded by other means and only referenced
            // as a dropdown source from city/service/country forms).
            // Don't reintroduce the page without first establishing
            // whether the team actually wants admin management here.
            {
                path: "documents",
                canActivate: [permissionGuard("documents:list")],
                loadComponent: () => import("./pages/documents-list/documents-list").then((m) => m.DocumentsListPage)
            },
            // ── Stock/SKU tight batch (June 2026) ──
            // sku-models (parent categories), skus (catalog with
            // sku-model picker wired against the sister service +
            // conditional scope inputs), spare-parts (garage catalog
            // with pricing HT/TVA/TTC). Heavier stock pages deferred:
            // purchase (1294 + 503 form), stock_movements (1993),
            // stock_location (543) need their own batches.
            {
                path: "sku-models",
                canActivate: [permissionGuard("sku_model:list")],
                loadComponent: () => import("./pages/sku-models-list/sku-models-list").then((m) => m.SkuModelsListPage)
            },
            // ── Maintenance SKU models (migration of legacy
            // /maintenance_sku_model) — list + form pair backed by the
            // shared @gozem-api/sku-model backend. Every record here is
            // tagged `created_from: "maintenance_page"`. Route ordering
            // matters: /maintenance-sku-models/new must match before
            // /maintenance-sku-models/:id.
            {
                path: "maintenance-sku-models",
                canActivate: [permissionGuard("sku_model:list")],
                loadComponent: () =>
                    import("./pages/maintenance-sku-models-list/maintenance-sku-models-list").then(
                        (m) => m.MaintenanceSkuModelsListPage
                    )
            },
            {
                path: "maintenance-sku-models/new",
                canActivate: [permissionGuard("sku_model:create")],
                loadComponent: () =>
                    import("./pages/maintenance-sku-models-form/maintenance-sku-models-form").then(
                        (m) => m.MaintenanceSkuModelsFormPage
                    )
            },
            {
                path: "maintenance-sku-models/:id",
                canActivate: [permissionGuard("sku_model:update")],
                loadComponent: () =>
                    import("./pages/maintenance-sku-models-form/maintenance-sku-models-form").then(
                        (m) => m.MaintenanceSkuModelsFormPage
                    )
            },
            {
                path: "skus",
                canActivate: [permissionGuard("skus:list")],
                loadComponent: () => import("./pages/skus-list/skus-list").then((m) => m.SkusListPage)
            },
            {
                path: "spare-parts",
                canActivate: [permissionGuard("spare_parts:list")],
                loadComponent: () =>
                    import("./pages/spare-parts-list/spare-parts-list").then((m) => m.SparePartsListPage)
            },
            {
                // Pause duration settings — list + modal CRUD with dynamic
                // availability rows. Mirrors gozem-admin-web's
                // /pause_duration_settings (279 LOC).
                path: "pause-duration-settings",
                canActivate: [permissionGuard("pause_duration_settings:list")],
                loadComponent: () =>
                    import("./pages/pause-duration-settings-list/pause-duration-settings-list").then(
                        (m) => m.PauseDurationSettingsListPage
                    )
            },

            // ──────────────────────────────────────────────────────────
            // City types — three legacy URL paths share the same template
            // (gozem-admin-web/app/views/admin/pages/city_type.html). v2
            // ships ONE component (CityTypesListPage) and routes it three
            // times with different `data: {mode}` values. The dedicated
            // form page mirrors the 8,435-LOC legacy
            // `add_city_type_form.html` — Phase 1 (July 2026) ships the
            // shell + cards 1-2; cards 3-9 land in phases 2-5.
            // ──────────────────────────────────────────────────────────
            {
                path: "city-types",
                canActivate: [permissionGuard("city_type:list")],
                loadComponent: () => import("./pages/city-types-list/city-types-list").then((m) => m.CityTypesListPage),
                data: {mode: "all"}
            },
            {
                path: "city-types/new",
                canActivate: [permissionGuard("city_type:create")],
                loadComponent: () => import("./pages/city-types-form/city-types-form").then((m) => m.CityTypesFormPage)
            },
            {
                path: "city-types/:id",
                canActivate: [permissionGuard("city_type:update")],
                loadComponent: () => import("./pages/city-types-form/city-types-form").then((m) => m.CityTypesFormPage)
            },
            // Wave 10 B19 — legacy /city_type/curate_section_form?id= (section curation of an
            // e-commerce service; also reached from the transport / e-commerce modes).
            {
                path: "city-types/:id/sections",
                canActivate: [permissionGuard("city_type:update")],
                loadComponent: () =>
                    import("./pages/type-section-curation/type-section-curation").then(
                        (m) => m.TypeSectionCurationPage
                    ),
                data: {kind: "city"}
            },
            {
                path: "transport-services",
                canActivate: [permissionGuard("city_type:list")],
                loadComponent: () => import("./pages/city-types-list/city-types-list").then((m) => m.CityTypesListPage),
                data: {mode: "transport"}
            },
            {
                path: "ecommerce-services",
                canActivate: [permissionGuard("city_type:list")],
                loadComponent: () => import("./pages/city-types-list/city-types-list").then((m) => m.CityTypesListPage),
                data: {mode: "ecommerce"}
            },

            // Bonus campaigns — list + row workflows (cancel modal,
            // payout confirm, change-logs viewer). Read-only on creation
            // since campaigns are authored from the BonusPrograms editor.
            {
                path: "bonus-campaigns",
                canActivate: [permissionGuard("bonus_campaigns:list")],
                loadComponent: () =>
                    import("./pages/bonus-campaigns-list/bonus-campaigns-list").then((m) => m.BonusCampaignsListPage)
            },
            // Champion bonuses — per-champion payout ledger. Read +
            // row workflows (cancel, change_payment_status, view trips).
            {
                path: "champion-bonuses",
                canActivate: [permissionGuard("champion_bonuses:list")],
                loadComponent: () =>
                    import("./pages/champion-bonuses-list/champion-bonuses-list").then((m) => m.ChampionBonusesListPage)
            },

            // ───── Validation workflow ─────
            {
                path: "validation-services",
                canActivate: [permissionGuard("validation_services:list")],
                loadComponent: () =>
                    import("./pages/validation-services-list/validation-services-list").then(
                        (m) => m.ValidationServicesListPage
                    )
            },
            {
                path: "validation-routes",
                canActivate: [permissionGuard("validation_routes:list")],
                loadComponent: () =>
                    import("./pages/validation-routes-list/validation-routes-list").then(
                        (m) => m.ValidationRoutesListPage
                    )
            },
            {
                // Read-only this batch. Full approval modal deferred.
                path: "validation-tasks",
                canActivate: [permissionGuard("validation_tasks:list")],
                loadComponent: () =>
                    import("./pages/validation-tasks-list/validation-tasks-list").then((m) => m.ValidationTasksListPage)
            },

            // Admin settings — singleton sectioned editor. 4 native
            // sections + an Advanced JSON fallback. The remaining 10
            // legacy sections (theme, push cert, email notif, etc.)
            // are reachable via the JSON editor until their own batches
            // ship.
            {
                path: "admin-settings",
                canActivate: [permissionGuard("settings:list")],
                loadComponent: () => import("./pages/admin-settings/admin-settings").then((m) => m.AdminSettingsPage)
            },

            // Settings batch — custom locations, page access, pid access,
            // installation settings.
            {
                path: "custom-locations",
                canActivate: [permissionGuard("custom_locations:list")],
                loadComponent: () =>
                    import("./pages/custom-locations-list/custom-locations-list").then((m) => m.CustomLocationsListPage)
            },
            {
                path: "page-access",
                canActivate: [permissionGuard("page_access:list")],
                loadComponent: () =>
                    import("./pages/page-access-list/page-access-list").then((m) => m.PageAccessListPage)
            },
            {
                path: "pid-access",
                canActivate: [permissionGuard("pid_access:list")],
                loadComponent: () => import("./pages/pid-access-list/pid-access-list").then((m) => m.PidAccessListPage)
            },
            {
                path: "installation-settings",
                canActivate: [permissionGuard("installation_settings:list")],
                loadComponent: () =>
                    import("./pages/installation-settings-page/installation-settings-page").then(
                        (m) => m.InstallationSettingsPage
                    )
            },

            // ───── Transport — list pages (trip-quotes, trip-correction-logs,
            // dynamic-surges, trip-correction-validation-settings) ─────
            {
                path: "trip-quotes",
                canActivate: [permissionGuard("trip_quotes:list")],
                loadComponent: () =>
                    import("./pages/trip-quotes-list/trip-quotes-list").then((m) => m.TripQuotesListPage)
            },
            {
                path: "trip-correction-logs",
                canActivate: [permissionGuard("trip_correction_logs:list")],
                loadComponent: () =>
                    import("./pages/trip-correction-logs-list/trip-correction-logs-list").then(
                        (m) => m.TripCorrectionLogsListPage
                    )
            },
            {
                path: "dynamic-surges",
                canActivate: [permissionGuard("dynamic_surges:list")],
                loadComponent: () =>
                    import("./pages/dynamic-surges-list/dynamic-surges-list").then((m) => m.DynamicSurgesListPage)
            },
            // Wave 10 B22 — legacy product_moderation (menu.json:117).
            {
                path: "product-moderation",
                canActivate: [permissionGuard("product_moderation:list")],
                loadComponent: () =>
                    import("./pages/product-moderation/product-moderation").then((m) => m.ProductModerationPage)
            },
            // Wave 10 B23 — legacy product_validation_settings (menu.json:126).
            {
                path: "product-validation-settings",
                canActivate: [permissionGuard("product_validation_settings:view")],
                loadComponent: () =>
                    import("./pages/product-validation-settings/product-validation-settings").then(
                        (m) => m.ProductValidationSettingsPage
                    )
            },
            {
                path: "trip-correction-validation-settings",
                canActivate: [permissionGuard("trip_correction_validation_settings:list")],
                loadComponent: () =>
                    import("./pages/trip-correction-validation-settings/trip-correction-validation-settings").then(
                        (m) => m.TripCorrectionValidationSettingsPage
                    )
            },

            // ───── Settings batch — expense-payment / budget-validation /
            // bug-ticket validation settings + global-settings ─────
            {
                path: "expense-payment-validation-settings",
                canActivate: [permissionGuard("expense_payment_validation_settings:list")],
                loadComponent: () =>
                    import("./pages/expense-payment-validation-settings/expense-payment-validation-settings").then(
                        (m) => m.ExpensePaymentValidationSettingsPage
                    )
            },
            {
                path: "budget-validation-tools-validation-settings",
                canActivate: [permissionGuard("budget_validation_tools_validation_settings:list")],
                loadComponent: () =>
                    import(
                        "./pages/budget-validation-tools-validation-settings/budget-validation-tools-validation-settings"
                    ).then((m) => m.BudgetValidationToolsValidationSettingsPage)
            },
            {
                path: "bug-ticket-validation-settings",
                canActivate: [permissionGuard("bug_ticket_validation_settings:list")],
                loadComponent: () =>
                    import("./pages/bug-ticket-validation-settings/bug-ticket-validation-settings").then(
                        (m) => m.BugTicketValidationSettingsPage
                    )
            },
            {
                path: "employee-validation-settings",
                canActivate: [permissionGuard("employee_validation_settings:list")],
                loadComponent: () =>
                    import("./pages/employee-validation-settings/employee-validation-settings").then(
                        (m) => m.EmployeeValidationSettingsPage
                    )
            },
            {
                path: "global-settings",
                canActivate: [permissionGuard("settings:list")],
                loadComponent: () =>
                    import("./pages/global-settings-page/global-settings-page").then((m) => m.GlobalSettingsPage)
            },
            // ── Marketing batch (June 2026) ──
            // user-promos: per-user promo-code allowance rows.
            // promo-code-usage: actual code-application history.
            // sms-logs / email-logs / push-notification-logs are already
            // served by the messaging-logs registry above.
            {
                path: "user-promos",
                canActivate: [permissionGuard("user_promos:list")],
                loadComponent: () =>
                    import("./pages/user-promos-list/user-promos-list").then((m) => m.UserPromosListPage)
            },
            {
                path: "promo-code-usage",
                canActivate: [permissionGuard("promo_code_usage:list")],
                loadComponent: () =>
                    import("./pages/promo-code-usage-list/promo-code-usage-list").then((m) => m.PromoCodeUsageListPage)
            },
            // ── Reports batch (June 2026) ──
            // Aggregated reports (single GET fetch with query params).
            // Cohort matrices + trip earning + transaction summary.
            {
                path: "trip-earning",
                canActivate: [permissionGuard("trip_earning:list")],
                loadComponent: () =>
                    import("./pages/trip-earning-report/trip-earning-report").then((m) => m.TripEarningReportPage)
            },
            {
                // Batch A#4 — v2 of legacy /merchant_earning (per-merchant
                // orders/wallet/catalogue report — verbatim aggregation port).
                path: "merchant-earnings",
                canActivate: [permissionGuard("merchant_earning:list")],
                loadComponent: () =>
                    import("./pages/merchant-earning-list/merchant-earning-list").then((m) => m.MerchantEarningListPage)
            },
            {
                path: "daily-earning",
                canActivate: [permissionGuard("daily_earning:list")],
                loadComponent: () =>
                    import("./pages/daily-earning-report/daily-earning-report").then((m) => m.DailyEarningReportPage)
            },
            {
                path: "weekly-earning",
                canActivate: [permissionGuard("weekly_earning:list")],
                loadComponent: () =>
                    import("./pages/weekly-earning-report/weekly-earning-report").then((m) => m.WeeklyEarningReportPage)
            },
            {
                path: "partner-weekly-earning",
                canActivate: [permissionGuard("partner_weekly_earning:list")],
                loadComponent: () =>
                    import("./pages/partner-weekly-earning-report/partner-weekly-earning-report").then(
                        (m) => m.PartnerWeeklyEarningReportPage
                    )
            },
            {
                path: "road-api-testing",
                canActivate: [permissionGuard("road_api_testing:list")],
                loadComponent: () =>
                    import("./pages/road-api-testing-page/road-api-testing-page").then((m) => m.RoadApiTestingPage)
            },
            {
                path: "fx-tools",
                canActivate: [permissionGuard("fx_tools:list")],
                loadComponent: () => import("./pages/fx-tools-list/fx-tools-list").then((m) => m.FxToolsListPage)
            },
            {
                path: "transaction-summary",
                canActivate: [permissionGuard("transaction_summary:list")],
                loadComponent: () =>
                    import("./pages/transaction-summary-report/transaction-summary-report").then(
                        (m) => m.TransactionSummaryReportPage
                    )
            },
            {
                path: "rider-cohorts",
                canActivate: [permissionGuard("rider_cohorts:list")],
                loadComponent: () =>
                    import("./pages/rider-cohorts-report/rider-cohorts-report").then((m) => m.RiderCohortsReportPage)
            },
            {
                path: "champion-cohorts",
                canActivate: [permissionGuard("champion_cohorts:list")],
                loadComponent: () =>
                    import("./pages/champion-cohorts-report/champion-cohorts-report").then(
                        (m) => m.ChampionCohortsReportPage
                    )
            },
            {
                path: "merchant-cohorts",
                canActivate: [permissionGuard("merchant_cohorts:list")],
                loadComponent: () =>
                    import("./pages/merchant-cohorts-report/merchant-cohorts-report").then(
                        (m) => m.MerchantCohortsReportPage
                    )
            },
            // ── RBAC v2 batch (July 2026) ──
            // Roles CRUD (list + dedicated form with two-pane permissions
            // picker), admin-role assignments (inline modal CRUD), and JIT
            // elevation requests (two-tab: mine + to-approve).
            //
            // These five RBAC routes are the ONLY ones gated by
            // `permissionGuard(...)` today — a fleet-wide guard on the
            // ~200 other routes is deliberately out of scope for this
            // pass. Legacy super-admins (`legacy_fallback: true`) short-
            // circuit `can(...)` and see every route regardless.
            {
                path: "rbac/roles",
                canActivate: [permissionGuard("roles:list")],
                loadComponent: () => import("./pages/rbac-roles-list/rbac-roles-list").then((m) => m.RbacRolesListPage)
            },
            {
                path: "rbac/roles/new",
                canActivate: [permissionGuard("roles:create")],
                loadComponent: () => import("./pages/rbac-roles-form/rbac-roles-form").then((m) => m.RbacRolesFormPage)
            },
            {
                path: "rbac/roles/:id",
                canActivate: [permissionGuard("roles:update")],
                loadComponent: () => import("./pages/rbac-roles-form/rbac-roles-form").then((m) => m.RbacRolesFormPage)
            },
            {
                path: "rbac/assignments",
                canActivate: [permissionGuard("admin_role_assignments:list")],
                loadComponent: () =>
                    import("./pages/rbac-assignments-list/rbac-assignments-list").then((m) => m.RbacAssignmentsListPage)
            },
            {
                // Deliberately unguarded — every authenticated admin may file
                // and follow their OWN elevation requests, and the API says so:
                // `GET`/`POST /rbac/elevation-requests` declare no token and are
                // on `fullPathWithoutPermissionCheck` (@gozem-api/security
                // permission_check.js; @gozem-api/rbac's route description
                // "Any authenticated admin may file one"). `?scope=all` is
                // authorised in the controller on `admin_role_assignments:update`,
                // and approve/deny declare the same token — the only thing on
                // this page that is gated.
                //
                // It was guarded on `elevation_requests:list`, a token no
                // endpoint enforces, so the shell's ElevationRequestModal sent
                // an admin who had just hit a JIT 401 to "follow my requests"
                // here and the guard bounced them to /access-denied. See
                // playwright/handover/wave3/page-resource.md.
                path: "rbac/elevation-requests",
                loadComponent: () =>
                    import("./pages/rbac-elevation-requests-list/rbac-elevation-requests-list").then(
                        (m) => m.RbacElevationRequestsListPage
                    )
            },
            // ── Access-denied landing ──
            // `permissionGuard` redirects here on failure. Full-screen
            // gz-status-page with a "Retour au tableau de bord" button.
            {
                path: "access-denied",
                loadComponent: () => import("./pages/access-denied/access-denied").then((m) => m.AccessDeniedPage)
            },
            // ── Hotels (retry — legacy /hotel migration) ──
            // List + form pair backed by @gozem-api/hotel. Ordering
            // matters: /hotels/new must match before /hotels/:id.
            {
                path: "hotels",
                canActivate: [permissionGuard("hotels:list")],
                loadComponent: () => import("./pages/hotels-list/hotels-list").then((m) => m.HotelsListPage)
            },
            {
                path: "hotels/new",
                canActivate: [permissionGuard("hotels:create")],
                loadComponent: () => import("./pages/hotels-form/hotels-form").then((m) => m.HotelsFormPage)
            },
            {
                path: "hotels/:id",
                canActivate: [permissionGuard("hotels:update")],
                loadComponent: () => import("./pages/hotels-form/hotels-form").then((m) => m.HotelsFormPage)
            },
            // ── Post categories (migration of legacy /post_categories) ──
            // List + form pair backed by @gozem-api/post-category. Ordering
            // matters: /post-categories/new must match before /post-categories/:id.
            {
                path: "post-categories",
                canActivate: [permissionGuard("post_categories:list")],
                loadComponent: () =>
                    import("./pages/post-categories-list/post-categories-list").then((m) => m.PostCategoriesListPage)
            },
            {
                path: "post-categories/new",
                canActivate: [permissionGuard("post_categories:create")],
                loadComponent: () =>
                    import("./pages/post-categories-form/post-categories-form").then((m) => m.PostCategoriesFormPage)
            },
            {
                path: "post-categories/:id",
                canActivate: [permissionGuard("post_categories:update")],
                loadComponent: () =>
                    import("./pages/post-categories-form/post-categories-form").then((m) => m.PostCategoriesFormPage)
            },
            // ── Partners (migration of legacy /partners) ──────────────
            // List + form pair backed by @gozem-api/partner. Ordering
            // matters: /partners/new must match before /partners/:id.
            // Legacy admin_routes/partner.js exposes `app.use('/partners', …)`
            // with list / detail / change-status; v2 exposes a plain
            // REST surface plus a dedicated PUT /partners/:id/status
            // for the approve/decline row action.
            {
                path: "partners",
                canActivate: [permissionGuard("partners:list")],
                loadComponent: () => import("./pages/partners-list/partners-list").then((m) => m.PartnersListPage)
            },
            {
                path: "partners/new",
                canActivate: [permissionGuard("partners:create")],
                loadComponent: () => import("./pages/partners-form/partners-form").then((m) => m.PartnersFormPage)
            },
            {
                path: "partners/:id",
                canActivate: [permissionGuard("partners:update")],
                loadComponent: () => import("./pages/partners-form/partners-form").then((m) => m.PartnersFormPage)
            },
            // ── Paying entities (migration of legacy /paying-entities) ──
            // List + form pair backed by @gozem-api/paying-entities (already
            // in gozem-api node_modules v1.1.2). Ordering matters:
            // /paying-entities/new must match before /paying-entities/:id.
            {
                path: "paying-entities",
                canActivate: [permissionGuard("paying_entities:list")],
                loadComponent: () =>
                    import("./pages/paying-entities-list/paying-entities-list").then((m) => m.PayingEntitiesListPage)
            },
            {
                path: "paying-entities/new",
                canActivate: [permissionGuard("paying_entities:create")],
                loadComponent: () =>
                    import("./pages/paying-entities-form/paying-entities-form").then((m) => m.PayingEntitiesFormPage)
            },
            {
                path: "paying-entities/:id",
                canActivate: [permissionGuard("paying_entities:update")],
                loadComponent: () =>
                    import("./pages/paying-entities-form/paying-entities-form").then((m) => m.PayingEntitiesFormPage)
            },
            // ── Maintenance SKUs (migration of legacy /maintenance_skus) ──
            // List + form pair backed by @gozem-api/maintenance-sku.
            // Ordering matters: /maintenance-skus/new must match before :id.
            {
                path: "maintenance-skus",
                canActivate: [permissionGuard("maintenance_skus:list")],
                loadComponent: () =>
                    import("./pages/maintenance-skus-list/maintenance-skus-list").then((m) => m.MaintenanceSkusListPage)
            },
            {
                path: "maintenance-skus/new",
                canActivate: [permissionGuard("maintenance_skus:create")],
                loadComponent: () =>
                    import("./pages/maintenance-skus-form/maintenance-skus-form").then((m) => m.MaintenanceSkusFormPage)
            },
            {
                path: "maintenance-skus/:id",
                canActivate: [permissionGuard("maintenance_skus:update")],
                loadComponent: () =>
                    import("./pages/maintenance-skus-form/maintenance-skus-form").then((m) => m.MaintenanceSkusFormPage)
            },
            // ── Employees (migration of legacy /employees) ──────────────
            // List + form pair backed by @gozem-api/employees. Belongs to
            // the Human Resources sidebar group. Ordering matters:
            // /employees/new must match before :id.
            {
                path: "employees",
                canActivate: [permissionGuard("employees:list")],
                loadComponent: () => import("./pages/employees-list/employees-list").then((m) => m.EmployeesListPage)
            },
            {
                path: "employees/new",
                canActivate: [permissionGuard("employees:create")],
                loadComponent: () => import("./pages/employees-form/employees-form").then((m) => m.EmployeesFormPage)
            },
            {
                path: "employees/:id",
                // Every guard must pass: edit needs :update, and the form loads GET /employees/:id (employees:view).
                canActivate: [permissionGuard("employees:update"), permissionGuard("employees:view")],
                loadComponent: () => import("./pages/employees-form/employees-form").then((m) => m.EmployeesFormPage)
            },
            // ── Maintenance templates (migration of legacy /maintenance_templates) ──
            // List + form pair backed by @gozem-api/maintenance-template.
            // Ordering matters: /maintenance-templates/new must match before :id.
            {
                path: "maintenance-templates",
                canActivate: [permissionGuard("maintenance_templates:list")],
                loadComponent: () =>
                    import("./pages/maintenance-templates-list/maintenance-templates-list").then(
                        (m) => m.MaintenanceTemplatesListPage
                    )
            },
            {
                path: "maintenance-templates/new",
                canActivate: [permissionGuard("maintenance_templates:create")],
                loadComponent: () =>
                    import("./pages/maintenance-templates-form/maintenance-templates-form").then(
                        (m) => m.MaintenanceTemplatesFormPage
                    )
            },
            {
                path: "maintenance-templates/:id",
                canActivate: [permissionGuard("maintenance_templates:update")],
                loadComponent: () =>
                    import("./pages/maintenance-templates-form/maintenance-templates-form").then(
                        (m) => m.MaintenanceTemplatesFormPage
                    )
            },
            // ── Alert Center (migration of legacy /alert_center) ────────
            // List + read-only detail backed by @gozem-api/alert-center.
            // Creation is DESIGN DEBT — see alert-center-form for the stub.
            // Ordering matters: /alert-center/new must match before :id.
            {
                path: "alert-center",
                canActivate: [permissionGuard("alert_center:list")],
                loadComponent: () =>
                    import("./pages/alert-center-list/alert-center-list").then((m) => m.AlertCenterListPage)
            },
            {
                path: "alert-center/new",
                canActivate: [permissionGuard("alert_center:create")],
                loadComponent: () =>
                    import("./pages/alert-center-form/alert-center-form").then((m) => m.AlertCenterFormPage)
            },
            {
                path: "alert-center/:id",
                canActivate: [permissionGuard("alert_center:view")],
                loadComponent: () =>
                    import("./pages/alert-center-form/alert-center-form").then((m) => m.AlertCenterFormPage)
            },
            // ── Checkout Discount Promotions (migration of legacy
            //    /checkout_discount_promotions) — list + read-only detail backed
            //    by @gozem-api/checkout-discount-promotion. Creation is DESIGN
            //    DEBT — see checkout-discount-promotions-form for the stub.
            //    Ordering matters: /checkout-discount-promotions/new must match
            //    before :id.
            {
                path: "checkout-discount-promotions",
                canActivate: [permissionGuard("checkout_discount_promotions:list")],
                loadComponent: () =>
                    import("./pages/checkout-discount-promotions-list/checkout-discount-promotions-list").then(
                        (m) => m.CheckoutDiscountPromotionsListPage
                    )
            },
            {
                path: "checkout-discount-promotions/new",
                canActivate: [permissionGuard("checkout_discount_promotions:create")],
                loadComponent: () =>
                    import("./pages/checkout-discount-promotions-form/checkout-discount-promotions-form").then(
                        (m) => m.CheckoutDiscountPromotionsFormPage
                    )
            },
            {
                path: "checkout-discount-promotions/:id",
                canActivate: [permissionGuard("checkout_discount_promotions:view")],
                loadComponent: () =>
                    import("./pages/checkout-discount-promotions-form/checkout-discount-promotions-form").then(
                        (m) => m.CheckoutDiscountPromotionsFormPage
                    )
            },
            // ── Entities tax status (migration of legacy /entities_tax_list) ─────
            // List + form backed by @gozem-api/admin-entity-taxs. RBAC gate on
            // `entity_tax_status:*` (kebab→snake fallback on the route segment).
            // Ordering matters: /entities-tax/new must match before /:id.
            {
                path: "entities-tax",
                canActivate: [permissionGuard("entity_tax_status:list")],
                loadComponent: () =>
                    import("./pages/entities-tax-list/entities-tax-list").then((m) => m.EntitiesTaxListPage)
            },
            {
                path: "entities-tax/new",
                canActivate: [permissionGuard("entity_tax_status:create")],
                loadComponent: () =>
                    import("./pages/entities-tax-form/entities-tax-form").then((m) => m.EntitiesTaxFormPage)
            },
            {
                path: "entities-tax/:id",
                canActivate: [permissionGuard("entity_tax_status:view")],
                loadComponent: () =>
                    import("./pages/entities-tax-form/entities-tax-form").then((m) => m.EntitiesTaxFormPage)
            },
            // ── Sandbox diagnostic (migration of legacy /sandbox) ──
            // Single search-only page backed by POST /sandbox/search
            // (@gozem-api/sandbox). Compares wallet_history vs
            // sandbox_wallet_history for a given trip / order /
            // financing contract / sold product. Read-only — no
            // /new or /:id routes.
            {
                path: "sandbox-search",
                canActivate: [permissionGuard("sandbox:list")],
                loadComponent: () => import("./pages/sandbox-search/sandbox-search").then((m) => m.SandboxSearchPage)
            },
            // ── Réconciliation des soldes (migration of legacy /reconciliations) ──
            // Single list page (two internal tabs: Comptes + Réconciliations)
            // backed by the existing routes in @gozem-api-cashflow-transactions
            // (routes/reconciliation.js — /api/v1/reconciliation/*). RBAC gate on
            // `reconciliations:list`; mutations are `high` + reason via
            // SENSITIVITY_OVERRIDES in @gozem/rbac.
            {
                path: "reconciliations",
                canActivate: [permissionGuard("reconciliations:list")],
                loadComponent: () =>
                    import("./pages/reconciliations-list/reconciliations-list").then((m) => m.ReconciliationsListPage)
            },
            // ── Debit source methods (migration of legacy /debit_source_methods) ──
            // List + form pair backed by @gozem-api/debit-source-method. Ordering
            // matters: /debit-source-methods/new must match before /:id.
            {
                path: "debit-source-methods",
                canActivate: [permissionGuard("debit_source_methods:list")],
                loadComponent: () =>
                    import("./pages/debit-source-methods-list/debit-source-methods-list").then(
                        (m) => m.DebitSourceMethodsListPage
                    )
            },
            {
                path: "debit-source-methods/new",
                canActivate: [permissionGuard("debit_source_methods:create")],
                loadComponent: () =>
                    import("./pages/debit-source-methods-form/debit-source-methods-form").then(
                        (m) => m.DebitSourceMethodsFormPage
                    )
            },
            {
                path: "debit-source-methods/:id",
                canActivate: [permissionGuard("debit_source_methods:update")],
                loadComponent: () =>
                    import("./pages/debit-source-methods-form/debit-source-methods-form").then(
                        (m) => m.DebitSourceMethodsFormPage
                    )
            },
            // ── ML Models (legacy /ml_models, GPGT-43599) ──
            // Pickup ETA model versions: upload to S3, set / stop shadow, notes — @gozem-api/ml-model.
            {
                path: "ml-models",
                canActivate: [permissionGuard("ml_models:list")],
                loadComponent: () => import("./pages/ml-models-list/ml-models-list").then((m) => m.MlModelsListPage)
            },
            // ── Mobile Agent blacklist (legacy /exclusion_lists, GPGT-41394) ──
            // List + create / edit modal backed by @gozem-api/exclusion-list.
            {
                path: "exclusion-lists",
                canActivate: [permissionGuard("exclusion_lists:list")],
                loadComponent: () =>
                    import("./pages/exclusion-lists-list/exclusion-lists-list").then((m) => m.ExclusionListsListPage)
            },
            // ── B2B daily opt-out configs (migration of legacy /b2b_daily_opt_out_configs) ──
            // List + form pair backed by @gozem-api/b2b-daily-opt-out-config. Ordering
            // matters: /b2b-daily-opt-out-configs/new must match before /:id.
            {
                path: "b2b-daily-opt-out-configs",
                canActivate: [permissionGuard("b2b_daily_opt_out_configs:list")],
                loadComponent: () =>
                    import("./pages/b2b-daily-opt-out-configs-list/b2b-daily-opt-out-configs-list").then(
                        (m) => m.B2bDailyOptOutConfigsListPage
                    )
            },
            {
                path: "b2b-daily-opt-out-configs/new",
                canActivate: [permissionGuard("b2b_daily_opt_out_configs:create")],
                loadComponent: () =>
                    import("./pages/b2b-daily-opt-out-configs-form/b2b-daily-opt-out-configs-form").then(
                        (m) => m.B2bDailyOptOutConfigsFormPage
                    )
            },
            {
                path: "b2b-daily-opt-out-configs/:id",
                canActivate: [permissionGuard("b2b_daily_opt_out_configs:update")],
                loadComponent: () =>
                    import("./pages/b2b-daily-opt-out-configs-form/b2b-daily-opt-out-configs-form").then(
                        (m) => m.B2bDailyOptOutConfigsFormPage
                    )
            },
            // ── Stale pickup orders (migration of legacy /stale_pickup_orders) ──
            // Monitoring/queue page backed by @gozem-api/stale-pickup-order.
            // Read-only list + inline cancel (reason required). No form page —
            // the underlying Order collection is edited through the /orders flow.
            {
                path: "stale-pickup-orders",
                canActivate: [permissionGuard("stale_pickup_orders:list")],
                loadComponent: () =>
                    import("./pages/stale-pickup-orders-list/stale-pickup-orders-list").then(
                        (m) => m.StalePickupOrdersListPage
                    )
            }
        ]
    },
    {path: "**", redirectTo: ""}
];
